import Ionicons from "@expo/vector-icons/Ionicons";
import { randomUUID } from "expo-crypto";
import {
  useLocalSearchParams,
  useRouter,
  type ErrorBoundaryProps,
} from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { practiceApi } from "@/api";
import { USE_MOCK } from "@/api/config";
import { ErrorFallback } from "@/components/ui/error-fallback";
import { KeyboardAvoidingContainer } from "@/components/ui/keyboard-view";
import { useKeyboardProbe } from "@/hooks/use-keyboard-probe";
import { CoachAudioButton } from "@/features/voice/coach-audio-button";
import { intakeClipId } from "@/features/voice/coach-audio-id";
import {
  DEFAULT_PRACTICE_VOICE,
  PRACTICE_VOICES,
} from "@/features/voice/practice-voice";
import {
  subscribePlayback,
  takePendingInterruption,
} from "@/features/voice/coach-playback";
import {
  useVoiceInput,
  type SttSessionEnd,
} from "@/features/voice/use-voice-input";
import { useVoiceMode } from "@/features/voice/use-voice-mode";
import { endTyping, noteTyping } from "@/features/voice/typing-lock";
import i18n from "@/i18n";
import { plusCurrencySymbol } from "@/constants/plans";
import { fetchBillingRegion } from "@/lib/billing-region";
import { getPaymentProvider } from "@/lib/payments";
import { canLoadOlder, moreBehind } from "@/lib/load-older";
import { createLatch } from "@/lib/single-tap";
import {
  SETTLE_MS,
  isFollowing,
  isFollowingInverted,
  practiceFocusPlan,
  type PracticeFocusForce,
} from "@/lib/chat-follow";
import { notify } from "@/lib/dialog";
import {
  guidedOpening,
  practicePackLabel,
  practiceScenarioCopy,
} from "@/lib/practice-labels";
import { probe } from "@/lib/timing-probe";
import { useAppTheme } from "@/theme/app-theme";
import type {
  ByoIntakeTurn,
  PracticeCredits,
  PracticeMessage,
  PracticeMode,
  PracticeVoice,
  PracticePack,
  PracticePackView,
  PracticeScenario,
  PracticeSession,
  PracticeSuggestedActionKind,
  PracticeSummary,
} from "@/types";

/** One selectable row in the cross-platform choice sheet. */
type ChoiceOption = {
  label: string;
  sublabel?: string;
  onPress?: () => void;
  destructive?: boolean;
  /** Marks the currently-active choice (e.g. the selected mode). */
  selected?: boolean;
  /** Shows a disclosure affordance even when the row has a sublabel. */
  showChevron?: boolean;
  /**
   * The row starts something slow instead of finishing something fast: the
   * sheet stays up, the row spins, and the handler owns closing it. Built for
   * the pack rows — closing on tap left seconds of bare practice screen
   * between the tap and the store's payment sheet, which read as the purchase
   * having silently failed.
   */
  stayOpen?: boolean;
};

/** Contents of the bottom sheet, or null when it is closed. */
type ChoiceSheet = {
  title: string;
  message?: string;
  variant?: "menu" | "picker";
  options: ChoiceOption[];
};

/**
 * How much transcript a page carries.
 *
 * Matches the server's default (`MESSAGE_PAGE_SIZE`). Stated here rather than
 * inferred, because `moreBehind` compares against it to decide whether the
 * session has earlier turns — a client guessing a different number would
 * either stop paging early or never stop.
 */
const PRACTICE_PAGE = 100;

type PracticeOpenerItem = {
  id: string;
  /**
   * A card rather than a message. `summary` is the end-of-session feedback,
   * which is a LIST ITEM and not a footer on purpose: the list auto-scrolls to
   * the newest bubble, and a footer taller than the screen therefore arrives
   * scrolled to its own bottom — the user meets the buttons and has to scroll up
   * to find out what the feedback said. As an item it can be framed by index.
   */
  kind: "modeIntro" | "opener" | "byoIntro" | "summary";
};

type PracticeListItem = PracticeMessage | PracticeOpenerItem;

/**
 * The id an optimistic user bubble carries until the turn completes.
 *
 * A CONSTANT rather than four spelt-out literals, because the four had drifted
 * apart: the bubble was minted as `local_${requestId}` while `loadOlder` and
 * `maybeLoadOlder` both asked `startsWith("local-")` — a hyphen. Nothing in the
 * screen has ever produced a `local-` id, so both guards were inert. The
 * consequence is not cosmetic: on a session whose only held message is the
 * optimistic one, reaching back sent `before=local_<uuid>` as the page cursor,
 * where the server resolves it against a uuid column, and `hasAnchor` reported
 * a real anchor when there was none.
 */
const OPTIMISTIC_PREFIX = "local_";
const isOptimistic = (id: string) => id.startsWith(OPTIMISTIC_PREFIX);

type SummaryPanel =
  | { status: "ready"; summary: PracticeSummary }
  | { status: "not_enough_practice"; reason: "no_practice" | "too_short" }
  | { status: "failed_retryable" };

const CHAT_MAX_WIDTH = 760;
const PRACTICE_MODES: PracticeMode[] = ["guided", "roleplay", "challenge"];

// Voice mode: end-of-utterance pause before auto-send (long enough for an
// ordinary thinking pause not to cut a slow talker off).
const VOICE_SILENCE_MS = 2000;

/**
 * How long a held-back coach reply waits for its voice before showing anyway.
 *
 * Raised from 5s. In hands-free mode the reply is kept out of the transcript
 * until its audio starts, so text and voice arrive together; this is the
 * ceiling on that wait. At 5s a slow turn crossed it routinely, the text
 * appeared alone, and the voice followed seconds later — the split that was
 * reported.
 *
 * Waiting longer is the cheap half of the fix, and it is safe because it only
 * bites when audio is SLOW. Audio that actually fails — TTS disabled, a 503, a
 * download error — rejects fast, and the loop's `finally` reveals the reply
 * immediately, so nobody watches a thinking bubble for ten seconds over a turn
 * that was never going to speak.
 *
 * Late audio is still played. Dropping it was tried and rejected: a turn that
 * loses its voice loses the thing the mode is for.
 *
 * The NUMBER is still a guess. `voice.audio.play` now measures what it is
 * guessing at.
 */
const HELD_REVEAL_CAP_MS = 12000;

/**
 * Keeps a render failure on this screen from taking the whole app down.
 *
 * A practice session is a conversation in progress, and its turns are on the
 * observability denylist — so the boundary reports that a failure happened
 * here without attaching any of what was said.
 */
export function ErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  return <ErrorFallback error={error} retry={retry} scope="practice" />;
}

export default function PracticeChatScreen() {
  useTranslation();
  const { colors, isDark } = useAppTheme();
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [session, setSession] = useState<PracticeSession | null>(null);
  const [scenario, setScenario] = useState<PracticeScenario | null>(null);
  const [pack, setPack] = useState<PracticePackView | null>(null);
  const [messages, setMessages] = useState<PracticeMessage[]>([]);
  // Completed sessions stay visible here for reference only; sends use the
  // current session id and never include these messages in the AI prompt.
  // Whether the transcript reaches further back than what is held. Set from
  // the size of the last page: a SHORT page means the session's first turn.
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [referenceMessages, setReferenceMessages] = useState<PracticeMessage[]>(
    [],
  );
  // Bring-your-own INTAKE turns (user ⇄ setup guide), shown in this same inbox
  // while a BYO session is still gathering the user's real moment. Once the
  // guide captures a scene (byoReady), the screen reloads into the roleplay.
  const [intake, setIntake] = useState<ByoIntakeTurn[]>([]);
  const [credits, setCredits] = useState<PracticeCredits | null>(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // A failed turn kept for retry. Retrying reuses the SAME requestId so the
  // backend dedupes it — no duplicate message and no double charge.
  const [retryTurn, setRetryTurn] = useState<{
    content: string;
    requestId: string;
  } | null>(null);
  const [summarizing, setSummarizing] = useState(false);
  const [summaryPanel, setSummaryPanel] = useState<SummaryPanel | null>(null);
  const [sheet, setSheet] = useState<ChoiceSheet | null>(null);
  // Label of the row whose slow work is in flight (a pack purchase reaching
  // the store). While set, the sheet refuses to dismiss and other rows are
  // inert — one purchase at a time, and no way to strand a spinner.
  const [sheetBusyLabel, setSheetBusyLabel] = useState<string | null>(null);
  const [initialModeChosen, setInitialModeChosen] = useState(false);
  const [practiceVoice, setPracticeVoice] =
    useState<PracticeVoice>(DEFAULT_PRACTICE_VOICE);
  const listRef = useRef<FlatList<PracticeListItem>>(null);
  // Where the end-of-session feedback sits in the list (-1 when absent), and
  // whether it has already been framed. Both are read by `refocusList`, which is
  // declared above where they are assigned — safe, because it only ever runs
  // from a layout callback or an effect, long after the render body.
  const summaryIndexRef = useRef(-1);
  const summaryFramedRef = useRef(false);
  const inputRef = useRef<TextInput>(null);
  // Measurement only — see `use-keyboard-probe.ts`. Practice was outside the
  // probe until build 18: the fault was only ever sampled on chat and group, so
  // a report from this screen had nothing behind it either way.
  const composerRef = useRef<View | null>(null);
  useKeyboardProbe(composerRef, "practice");
  const scrollFrameRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(
    null,
  );
  const scrollSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  /**
   * Is the reader at the bottom of the transcript, and has the content height
   * stopped moving? The two things `practiceFocusPlan` cannot work out for itself.
   *
   * Both start optimistic for the same reasons as `use-thread-follow.ts`: a
   * list with nothing in it yet is trivially at its own bottom, and has not
   * finished arriving. `settleWindowRef` is the deadline that decides the
   * second one — re-opened by every growth, so a transcript counts as settled
   * only once its height has actually held still.
   */
  const followingRef = useRef(true);
  /**
   * The ref's render-visible mirror, and both are needed for the same reason as
   * in `use-thread-follow.ts`: `onScroll` must read and write synchronously
   * without a render per scroll event, while `maintainVisibleContentPosition`
   * has to reach the list as a PROP. `setFollowing` bails out on an unchanged
   * value, so re-renders are bounded by threshold crossings rather than by
   * scroll traffic.
   */
  const [following, setFollowing] = useState(true);
  const settledRef = useRef(false);
  const settleWindowRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Whether the LIST currently on screen is inverted, read at the moment a
   * scroll command executes rather than from a callback's closure.
   *
   * The transcript inverts only once it has a conversation (see `listData`),
   * and the first send is exactly the race this ref exists for: `forceFocus`
   * runs before React commits the optimistic message, so the `refocusList`
   * identity it calls still closes over `hasConversation: false` — while the
   * frame the scroll fires on is already inverted. Branching on the closure
   * would aim the landing at the wrong end of the list, once, on every
   * session's first line. Assigned in the render body so it is always the
   * committed geometry by the time any queued frame runs.
   */
  const invertedRef = useRef(false);
  // Controller + requestId for the in-flight reply so the user can stop it
  // (locally and durably on the server) and send again.
  const abortRef = useRef<AbortController | null>(null);
  const inFlightRequestId = useRef<string | null>(null);
  const [stopped, setStopped] = useState(false);

  const ended = session?.status !== "active";
  // BYO intake phase: a bring-your-own session that hasn't captured its scene
  // yet. The session is still "active", so `ended` stays false and the composer
  // + Menu stay available — we just talk to the setup guide.
  const inIntake = session?.sceneSource === "byo" && !session?.byoReady;
  const hasSummary = Boolean(
    session?.summaryData || summaryPanel?.status === "ready",
  );
  const remaining = credits?.remainingReplies ?? 0;
  const activeSessionId = session?.id ?? id;
  // Whether the list has an actual conversation to follow (roleplay turns, or a
  // BYO recount with the setup guide) as opposed to just an intro/scenario card.
  // Decides which end of the list `refocusList` frames. This flips at most once
  // per session, so depending on it costs one extra `refocusList` identity.
  const hasConversation = messages.length > 0 || intake.length > 0;

  const goBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.replace("/practice");
  }, [router]);

  /**
   * Re-frame the list after its content changes.
   *
   * Which of the three things to frame — the newest bubble, the top of the
   * scenario card, the top of the end-of-session feedback — and whether to
   * animate getting there is `practiceFocusPlan`, in `chat-follow.ts`, with the
   * reasoning and the reader rules. This is the wiring: it reads the two things
   * the plan cannot see (where the reader is, whether the content is still
   * moving) and performs whatever comes back.
   *
   * The scroll itself waits a frame, because the callbacks that ask for it fire
   * while React is still committing the rows it will measure.
   */
  const refocusList = useCallback(
    (forced: PracticeFocusForce = false) => {
      const summaryIndex = summaryIndexRef.current;
      // Decided HERE, not inside the frame below: the caller knows whether the
      // content has been holding still, and `onContentSizeChange` re-opens that
      // window the moment it has asked for this. Deciding a frame later would
      // read the answer to the next question.
      const plan = practiceFocusPlan({
        forced,
        settled: settledRef.current,
        following: followingRef.current,
        hasConversation,
        hasSummary: summaryIndex >= 0,
        summaryFramed: summaryFramedRef.current,
      });
      if (plan.target === "none") return;
      if (plan.target === "summary") summaryFramedRef.current = true;

      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
      if (scrollSettleTimerRef.current !== null) {
        clearTimeout(scrollSettleTimerRef.current);
      }

      const apply = () => {
        if (plan.target === "summary") {
          // Framing the feedback's TOP means opposite viewPositions in the two
          // geometries. Non-inverted, 0 puts the item's scroll-start — its top
          // — at the viewport's top. Inverted, the item's scroll-start is its
          // visual BOTTOM, and viewPosition 1 is what lands its far end — the
          // visual top — at the viewport's top, clamped to a no-op when the
          // whole card fits. Verified against react-native-web's own frame
          // math in Chromium: a 650px summary in a 400px viewport framed at
          // exactly its first line.
          listRef.current?.scrollToIndex({
            index: summaryIndex,
            viewPosition: invertedRef.current ? 1 : 0,
            animated: plan.animated,
          });
          return;
        }
        if (plan.target === "end") {
          if (invertedRef.current) {
            // The newest line of an inverted transcript is offset 0, which
            // never moves — no confirm loop to chase a growing end. The loop
            // this replaces, and the three ways a single scrollToEnd missed,
            // are documented in use-thread-follow.ts.
            listRef.current?.scrollToOffset({ offset: 0, animated: false });
            return;
          }
          // Only reachable pre-flip: an "end" decided while the list still
          // renders card-first, executed before the conversation's first
          // commit. One best-effort shot — the content-size change that
          // commit produces re-frames through the inverted branch above.
          listRef.current?.scrollToEnd({ animated: false });
          return;
        }
        listRef.current?.scrollToOffset({ offset: 0, animated: plan.animated });
      };

      scrollFrameRef.current = requestAnimationFrame(() => {
        apply();
        // The summary is framed by INDEX, and `scrollToIndex` needs that row
        // measured; this re-assert is what covers a summary taller than the
        // render window (see `onScrollToIndexFailed`). The other two targets no
        // longer need it — while the content is still growing, every size
        // change re-pins them.
        if (plan.target === "summary") {
          scrollSettleTimerRef.current = setTimeout(apply, 90);
        }
      });
    },
    [hasConversation],
  );

  /**
   * Put the reader where the thing they just asked for is.
   *
   * Sending a line, choosing a mode, swapping the scene: an explicit ask, so it
   * outranks both the settle window and wherever the reader had scrolled to —
   * and it re-opens the window, because the new content is about to lay out and
   * should be landed on instantly rather than glided to. That instant landing
   * is the flicker fix from `chat-follow.ts` arriving here.
   */
  const forceFocus = useCallback(
    (target: Exclude<PracticeFocusForce, false>) => {
      followingRef.current = true;
      setFollowing(true);
      settledRef.current = false;
      refocusList(target);
    },
    [refocusList],
  );

  /**
   * Re-frame, then re-open the settle window.
   *
   * A virtualized list reports its height several times while rendering rows,
   * so "the height has held still for a moment" is the only honest signal that
   * the content has finished arriving — there is no callback for it.
   *
   * The window slides: EVERY growth re-opens it, not just the ones during the
   * opening layout. That is what keeps voice mode sane. The interim-transcript
   * bubble grows on every STT partial (see `onText: setVoiceInterim`), far
   * faster than this deadline, so the transcript correctly reads as "still
   * moving" and each partial re-pins instantly instead of starting an animation
   * that the next partial interrupts. A single message arriving into a quiet
   * transcript still animates, because it is decided before this runs.
   */
  const onContentSizeChange = useCallback((_width: number, height: number) => {
    // MEASUREMENT, matching `use-thread-follow.ts`. The height before and after
    // a turn is what says whether anything actually moved — practice was the
    // one thread with no instrumentation at all, which is why the build-16
    // capture has nothing to say about the jumps filmed on it.
    probe("practice.sizeChange", { h: Math.round(height) });
    refocusList();
    settledRef.current = false;
    if (settleWindowRef.current) clearTimeout(settleWindowRef.current);
    settleWindowRef.current = setTimeout(() => {
      settledRef.current = true;
    }, SETTLE_MS);
  }, [refocusList]);

  /**
   * Synchronous, unlike `loadingOlder`.
   *
   * `loadingOlder` is React state and the scroll handler runs at 60fps, so the
   * value it reads does not change until React commits — one flick therefore
   * issued one request per frame, every one of them carrying the SAME `before`
   * cursor. `mergeMessages` hid that on this thread by deduping on id; the
   * practice transcript prepends its page directly and duplicated turns.
   *
   * The same latch, against the same mistake, as the auth submits in
   * `lib/single-tap.ts`. The state flag stays, but only to draw the header.
   */
  const olderLatch = useRef(createLatch()).current;

  /**
   * The page BEFORE the oldest turn held.
   *
   * Opening a session reads the newest page; this is how the rest of the
   * transcript is reached. Without it, bounding that read would make earlier
   * turns unreachable rather than merely unloaded.
   */
  const loadOlder = useCallback(async () => {
    if (!id) return;
    const oldest = messages.find((message) => !isOptimistic(message.id));
    if (!oldest) return;

    if (!olderLatch.claim()) return;

    setLoadingOlder(true);
    try {
      const { messages: older } = await practiceApi.olderMessages(id, {
        before: oldest.id,
        limit: PRACTICE_PAGE,
      });
      // Prepended rather than merged by timestamp: a page from `before` is
      // strictly older than everything held, and the server never returns the
      // cursor row itself.
      //
      // Still filtered by id. The latch makes a duplicate request very
      // unlikely, but "unlikely" is the wrong guarantee for a transcript: a
      // duplicated turn reads as the model repeating itself, which is a
      // content bug rather than a visible glitch. The one-to-one and group
      // threads get this for free from `mergeMessages`; this list does not.
      setMessages((prev) => {
        const held = new Set(prev.map((message) => message.id));
        const fresh = older.filter((message) => !held.has(message.id));
        return fresh.length === 0 ? prev : [...fresh, ...prev];
      });
      setHasOlder(moreBehind(older.length, PRACTICE_PAGE));
    } catch {
      // Left as it was, so scrolling back up simply tries again.
    } finally {
      setLoadingOlder(false);
      olderLatch.release();
    }
  }, [id, messages, olderLatch]);

  /**
   * Reaching back, decided by the list reporting its far end. The guards are
   * `lib/load-older.ts`, shared with the group and one-to-one threads so the
   * three stop diverging. While the transcript is card-only the far end is the
   * card's bottom — and `hasAnchor` is what keeps that from meaning anything,
   * because with no real turn held there is no page to ask for.
   */
  const maybeLoadOlder = useCallback(() => {
    const anchored = messages.some(
      (message) => !isOptimistic(message.id),
    );
    if (
      !canLoadOlder({
        loading: olderLatch.held,
        hasOlder,
        hasAnchor: anchored,
      })
    ) {
      return;
    }
    void loadOlder();
  }, [hasOlder, loadOlder, messages, olderLatch]);

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent;
      // Two geometries, one question. Inverted, the newest line is offset 0
      // and the offset alone answers it; card-first, the bottom still has to
      // be computed from the content height. The old "ignore our own travel"
      // guard went with the confirm loop that produced that travel — the
      // inverted snap emits no journey, only its destination.
      followingRef.current = invertedRef.current
        ? isFollowingInverted(contentOffset.y)
        : isFollowing({
            offsetY: contentOffset.y,
            viewportHeight: layoutMeasurement.height,
            contentHeight: contentSize.height,
          });
      setFollowing(followingRef.current);
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
      if (scrollSettleTimerRef.current !== null) {
        clearTimeout(scrollSettleTimerRef.current);
      }
      if (settleWindowRef.current !== null) {
        clearTimeout(settleWindowRef.current);
      }
    };
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [view, bal, scenarios] = await Promise.all([
        practiceApi.getSession(id),
        practiceApi.credits(),
        practiceApi.scenarios(),
      ]);
      setSession(view.session);
      setReferenceMessages([]);
      // BYO sessions still gathering their scene run the intake conversation in
      // this same inbox (see `inIntake`); load whatever intake has happened.
      setIntake(view.intake ?? []);
      setSummaryPanel(
        view.session.summaryData
          ? { status: "ready", summary: view.session.summaryData }
          : null,
      );
      // The NEWEST page of the transcript, not all of it. Every message is
      // encrypted at rest and decrypted per row, so this read was unbounded
      // CPU on the API for a screen that opens at the turn you were on.
      const loadedMessages = view.messages;
      setMessages(loadedMessages);
      setHasOlder(moreBehind(loadedMessages.length, PRACTICE_PAGE));
      setInitialModeChosen(
        loadedMessages.length > 0 ||
          view.session.status !== "active" ||
          view.session.practiceMode !== "guided",
      );
      setPack(view.pack ?? null);
      setCredits(bal);
      setScenario(
        scenarios.find((s) => s.id === view.session.scenarioId) ?? null,
      );
    } catch {
      notify(
        i18n.t("practice.practiceAlert"),
        i18n.t("practice.errors.sessionLoad"),
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void load();
    }, 0);

    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (loading) return;
    // Choosing a mode swaps the card underneath the reader, so that one is an
    // explicit re-frame. The first paint after loading is not: the list's own
    // layout and content-size callbacks drive it, which is the whole point of
    // the settle window — an effect keyed on the message array runs BEFORE the
    // rows have laid out, so it computes an offset from a height still growing.
    // That effect is what made the one-to-one and group threads open at the top
    // at random, and `messages.length` and `sending` are gone from here for the
    // same reason: a send re-frames itself, and the thinking bubble is content,
    // to be followed only if the reader is at the bottom.
    //
    // `forceFocus` now writes the follow MIRROR as well as the ref, so this
    // line sets state from an effect. The alternative — a ref-only variant for
    // the effect to call — was rejected: it would be correct only while an
    // argument holds about which handler runs before which effect, and a ref
    // and its mirror drifting apart is precisely the defect the mirror exists
    // to prevent. The cost here is bounded and the common case is free: React
    // bails out when the value is unchanged, which it is on every mount, and
    // the one case where it does change — the reader scrolled away and the
    // geometry then flipped — is a re-render we require, because the anchor
    // reaches the list as a prop.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above: keeping the follow ref and its prop mirror in lockstep outranks the cascading-render heuristic here.
    if (initialModeChosen) forceFocus(hasConversation ? "end" : "top");
    else refocusList();
  }, [initialModeChosen, loading, hasConversation, refocusList, forceFocus]);

  const showWarning = (warning: PracticeCredits["warning"]) => {
    if (warning === "five_left") {
      notify(i18n.t("practice.practiceAlert"), i18n.t("practice.repliesFive"));
    } else if (warning === "two_left") {
      notify(i18n.t("practice.practiceAlert"), i18n.t("practice.repliesTwo"));
    }
  };

  // Voice-mode plumbing read inside `send` without widening its deps:
  // `voiceModeRef` mirrors the voice-mode flag, `voiceRetryRef` counts auto
  // retries for the current turn, and `sendRef` lets a delayed retry call the
  // latest `send`.
  const voiceModeRef = useRef(false);
  const voiceRetryRef = useRef(0);
  const sendRef = useRef<
    | ((arg?: {
        content: string;
        requestId?: string;
        retry?: boolean;
        fromVoice?: boolean;
      }) => Promise<void>)
    | null
  >(null);
  // Set later; starts holding a just-arrived coach reply out of the transcript
  // until its voice plays (the "thinking" hack). No-op until assigned.
  const holdCoachRef = useRef<(id: string) => void>(() => {});

  const send = useCallback(
    async (arg?: {
      content: string;
      requestId?: string;
      retry?: boolean;
      fromVoice?: boolean;
    }) => {
      const isRetry = arg?.retry ?? false;
      // A retry reuses the failed turn's id; every fresh turn mints a new one. A
      // voice turn carries its own transcript; a typed turn reads the composer.
      const turn = isRetry
        ? { content: arg!.content, requestId: arg!.requestId! }
        : {
            content: (arg?.fromVoice ? arg.content : input).trim(),
            requestId: randomUUID(),
          };
      if (!turn.content || sending || !activeSessionId || ended) return;
      if (remaining <= 0) return;

      // Attach approximate barge-in metadata for a fresh turn (§11). A retry
      // reuses the existing message, which already carries it.
      const interruption = isRetry ? undefined : takePendingInterruption() ?? undefined;
      const optimisticId = `${OPTIMISTIC_PREFIX}${turn.requestId}`;
      const controller = new AbortController();
      abortRef.current = controller;
      inFlightRequestId.current = turn.requestId;
      setSending(true);
      setStopped(false);
      setRetryTurn(null);
      if (summaryPanel?.status !== "ready") setSummaryPanel(null);
      if (!isRetry) {
        // Only a TYPED turn consumes the composer draft; a VOICE turn leaves any
        // half-typed draft untouched (the two are separate input channels).
        if (!arg?.fromVoice) {
          setInput("");
          endTyping(); // typed message sent → release the mic for the next turn
        }
        const optimistic: PracticeMessage = {
          id: optimisticId,
          role: "user",
          content: turn.content,
          createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, optimistic]);
        // Sending IS the ask to be at the newest line, so it re-frames itself
        // rather than relying on an effect keyed on the message array. It also
        // covers the reader who had scrolled up and then sent: they get taken
        // to their own message, which following alone would refuse to do.
        forceFocus("end");
      }

      // MEASUREMENT: the missing half of the voice-latency question. What the
      // coach's voice costs has been measured (`voice.audio.play`, 1.9s and
      // 3.5s on build 16) but that span starts once the reply is already in
      // hand — the model's own turn, which precedes it and cannot overlap it,
      // has never been recorded at all. Until it is, "the voice takes too long"
      // cannot be attributed to the model or to TTS, and those have completely
      // different fixes.
      const askedAt = Date.now();
      try {
        const res = await practiceApi.sendMessage(
          activeSessionId,
          turn.content,
          turn.requestId,
          controller.signal,
          interruption,
        );
        // `chars` is the covariate that makes the TTS number readable — synthesis
        // time scales with the text — and a length is not content: the probe
        // contract carries finite numbers only, and these lines never leave the
        // device.
        probe("practice.reply", {
          ms: Date.now() - askedAt,
          chars: res.message.content.length,
          voice: voiceModeRef.current ? 1 : 0,
          intake: 0,
        });
        // In voice mode, hold the reply out of the transcript until its voice
        // plays (same state batch as appending, so there's no flash of text).
        if (voiceModeRef.current) holdCoachRef.current(res.message.id);
        setMessages((prev) => [...prev, res.message]);
        setCredits(res.credits);
        showWarning(res.warning);
        voiceRetryRef.current = 0; // clean turn — reset the auto-retry budget
      } catch (err) {
        if (controller.signal.aborted) {
          // The user stopped this request. Drop the optimistic bubble quietly
          // so they can send a new message; no alert, no retry banner.
          setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
        } else if (
          err instanceof Error &&
          /out_of_practice_replies/.test(err.message)
        ) {
          // No credits: nothing was charged; drop the optimistic bubble.
          setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
          notify(
            i18n.t("practice.practiceAlert"),
            i18n.t("practice.ai.noCredits"),
          );
        } else if (voiceModeRef.current && voiceRetryRef.current < 1) {
          // Hands-free voice mode: a stalled loop is dead air, so silently
          // auto-retry the same turn once before surfacing the retry banner.
          voiceRetryRef.current += 1;
          setTimeout(() => void sendRef.current?.({ ...turn, retry: true }), 1500);
        } else {
          // Temporary failure (incl. rate limits): keep the user's message and
          // offer an inline, same-requestId retry (no blocking alert).
          voiceRetryRef.current = 0;
          setRetryTurn(turn);
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        if (inFlightRequestId.current === turn.requestId) {
          inFlightRequestId.current = null;
        }
        setSending(false);
      }
    },
    [
      input,
      sending,
      activeSessionId,
      ended,
      remaining,
      summaryPanel?.status,
      forceFocus,
    ],
  );
  useEffect(() => {
    sendRef.current = send;
  });

  /**
   * BYO intake turn — the user recounts (or refines) their real moment and the
   * setup guide replies, all in this same inbox. It's free (no credit gate) and
   * routes to the intake endpoint, not the roleplay one. When the guide signals
   * `ready`, the scene is captured server-side and we reload into the roleplay.
   */
  const sendIntake = useCallback(
    async (arg?: { content?: string; fromVoice?: boolean }) => {
      const content = (arg?.fromVoice ? arg.content ?? "" : input).trim();
      if (!content || sending || !activeSessionId) return;
      setSending(true);
      setStopped(false);
      if (!arg?.fromVoice) {
        setInput("");
        endTyping();
      }
      // Optimistically show the user's line; the guide's reply follows.
      setIntake((prev) => [...prev, { role: "user", content }]);
      forceFocus("end"); // same explicit ask as a roleplay turn
      // The OTHER turn path, and the one the build-17 capture fell down. Only
      // `send` was instrumented, the session recorded was BYO in intake, so
      // every turn went through here and `practice.reply` never fired — the
      // model's own turn came back unmeasured from a capture taken to measure
      // it. `intake: 1` keeps the two paths on one timeline while still telling
      // them apart.
      const askedAt = Date.now();
      try {
        const res = await practiceApi.intakeTurn(activeSessionId, content);
        probe("practice.reply", {
          ms: Date.now() - askedAt,
          chars: res.say.length,
          voice: voiceModeRef.current ? 1 : 0,
          intake: 1,
        });
        setIntake((prev) => [...prev, { role: "guide", content: res.say }]);
        setCredits(res.credits); // intake spends a reply from the 2nd call on
        if (res.ready) {
          // Scene captured — reload this same screen into the roleplay phase
          // (byoReady is now true, so `inIntake` flips false).
          await load();
        }
      } catch (err) {
        if (err instanceof Error && /out_of_practice_replies/.test(err.message)) {
          // No credits: nothing was charged. Drop the optimistic line and tell
          // them plainly, rather than leaving a dead-end error bubble.
          setIntake((prev) => prev.slice(0, -1));
          notify(i18n.t("practice.practiceAlert"), i18n.t("practice.ai.noCredits"));
        } else {
          setIntake((prev) => [
            ...prev,
            { role: "guide", content: i18n.t("practice.byo.error") },
          ]);
        }
      } finally {
        setSending(false);
      }
    },
    [input, sending, activeSessionId, load, forceFocus],
  );

  // Refs so the once-created voice callbacks can route to the right send path
  // (intake vs roleplay) without being torn down and rebuilt each turn.
  const inIntakeRef = useRef(false);
  useEffect(() => {
    inIntakeRef.current = inIntake;
  });
  const sendIntakeRef = useRef<typeof sendIntake | null>(null);
  useEffect(() => {
    sendIntakeRef.current = sendIntake;
  });

  /**
   * Stop the in-flight reply. Aborts the local request AND tells the backend to
   * cancel the turn so a reply that finishes server-side is discarded and no
   * credit is charged. If the cancel call fails, we still stop locally; the
   * backend's turn timeout finalizes it safely.
   */
  const stop = useCallback(() => {
    const requestId = inFlightRequestId.current;
    abortRef.current?.abort();
    setStopped(true);
    if (requestId) void practiceApi.cancelTurn(requestId).catch(() => {});
  }, []);

  // Voice Practice (Phase B): hands-free VOICE MODE. Tapping the mic turns it on
  // and it stays on until tapped off. While on, the conversation is spoken: the
  // mic auto-captures, the live transcript shows in a transient "listening"
  // bubble (its own channel — the typed composer is left alone), it auto-sends
  // after a natural end-of-utterance pause, and each coach reply auto-plays; the
  // mic then resumes. See `useVoiceMode` for the loop. Only where an STT path
  // exists (fail-closed).
  const [voiceMode, setVoiceMode] = useState(false);
  // The live voice transcript, shown as a transient "listening" bubble in the
  // message list (NOT the composer — voice and typing are separate channels, so
  // a typed draft is never overwritten). Cleared when the turn sends (the real
  // optimistic message takes over), the session ends empty, or voice mode is off.
  const [voiceInterim, setVoiceInterim] = useState("");
  // Deps matter here more than usual. Without them this effect ran after EVERY
  // render, and in voice mode `onText` sets state on every STT partial — so the
  // highest-frequency render path in the app was also re-running an effect that
  // attempted a state write each time. Dropping the ghost moved to the toggle
  // that turns voice mode off, which is where the decision is actually made.
  //
  // The toggle is not the only way voice mode ends: `useVoiceMode` also turns
  // itself off on a hard STT error or a run of failed mic starts. That path
  // needs no clear of its own — it lives inside `onSessionEnd`, which returns
  // early when the session captured anything, so it can only run for a
  // `captured: false` end, and `onSessionEnd` below already clears the ghost
  // before delegating to it. That clear is the guarantee — NOT the render gate
  // `voice.listening || voice.pendingSend`, which is only half shut at that
  // instant: `useVoiceInput`'s `onEnd` sets `listening` false before calling
  // `onSessionEnd` but `pendingSend` false after it (its `onError` does the
  // opposite), so the ordering there is incidental and must not be leaned on.
  // Nothing observes the gap regardless — it is all one synchronous callback,
  // and React batches it into a single render. No other path writes
  // `voiceInterim`: the mic is only ever started by `useVoiceMode`, and only
  // while active. Guarded by `tests/voice/ghost-clear.test.ts`.
  useEffect(() => {
    voiceModeRef.current = voiceMode;
  }, [voiceMode]);
  const sessionEndRef = useRef<(result: SttSessionEnd) => void>(() => {});
  const voice = useVoiceInput({
    onText: setVoiceInterim,
    onSend: (text) => {
      setVoiceInterim("");
      // Spoken recount during intake goes to the setup guide; otherwise it's a
      // roleplay turn.
      if (inIntakeRef.current) {
        void sendIntakeRef.current?.({ content: text, fromVoice: true });
      } else {
        void send({ content: text, fromVoice: true });
      }
    },
    silenceMs: VOICE_SILENCE_MS,
    onSessionEnd: (result) => {
      if (!result.captured) setVoiceInterim(""); // nothing to send — drop the ghost
      sessionEndRef.current(result);
    },
    // Keep the mic warm while voice mode is on so each turn starts capturing
    // instantly (no per-turn getUserMedia/permission delay).
    warm: voiceMode,
  });

  // The coach reply to speak: the last message only when it's a coach turn
  // (else it's the user's turn / nothing to play). Drives auto-play.
  const coachReplyToPlay = useMemo(() => {
    // Once the roleplay has any messages, the spoken reply is the last coach
    // turn (else it's the user's turn / nothing to play).
    const last = messages[messages.length - 1];
    if (last) return last.role === "coach" ? last.id : null;
    // No roleplay messages yet. In a BYO session the last setup-guide turn —
    // including the hand-off line — is what should be spoken, routed by its
    // intake clip id. Deriving this from messages (not `inIntake`) keeps the id
    // STABLE across the intake→roleplay hand-off: without that, the id would
    // flip to null the instant `byoReady` lands, cancelling the in-flight
    // playback and leaving voice mode on but the mic never resuming. Stable id
    // ⇒ playback finishes and listening resumes straight into the scene.
    if (session?.sceneSource === "byo") {
      const lastIdx = intake.length - 1;
      const guide = intake[lastIdx];
      return guide && guide.role === "guide"
        ? intakeClipId(session?.id ?? id, lastIdx)
        : null;
    }
    return null;
  }, [intake, messages, session?.sceneSource, session?.id, id]);

  // In voice mode the coach reply is held out of the transcript until its voice
  // actually starts, so the reply and its audio arrive together rather than
  // text, silence, and then a late voice.
  //
  // ONE ARRIVAL PER TURN. The reveal has exactly three causes, and the loop
  // owns all three: audio started, audio settled, or the loop gave up on it
  // (`AUDIO_DEADLINE_MS`). The cap below is no longer the normal path — it is a
  // backstop for the loop never calling back at all, e.g. voice mode switched
  // off mid-flight — which is why it sits ABOVE the loop's own deadline. When
  // the loop abandons a turn it also stops the in-flight audio, so a late clip
  // can no longer talk over a reply the reader has already been given.
  const [heldCoachId, setHeldCoachId] = useState<string | null>(null);
  const heldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const revealCoach = useCallback((id?: string) => {
    if (heldTimerRef.current) {
      clearTimeout(heldTimerRef.current);
      heldTimerRef.current = null;
    }
    setHeldCoachId((cur) => (id === undefined || cur === id ? null : cur));
  }, []);
  const holdCoach = useCallback((id: string) => {
    setHeldCoachId(id);
    if (heldTimerRef.current) clearTimeout(heldTimerRef.current);
    heldTimerRef.current = setTimeout(() => {
      heldTimerRef.current = null;
      setHeldCoachId((cur) => (cur === id ? null : cur));
    }, HELD_REVEAL_CAP_MS);
  }, []);
  useEffect(() => {
    holdCoachRef.current = holdCoach;
  });

  const voiceModeApi = useVoiceMode({
    active: voiceMode,
    supported: voice.supported,
    coachReplyToPlay,
    startListening: voice.start,
    stopListening: voice.stop,
    setActive: setVoiceMode,
    onSpeakStart: revealCoach,
    onSpeakEnd: revealCoach,
    voice: practiceVoice,
    // The loop gave up after repeated sessions where the mic never opened. Each
    // one on its own is a code we keep quiet about; the run of them is not, and
    // silently flickering forever would leave the user with no idea whether to
    // wait, retry, or type.
    onUnavailable: () =>
      voice.showNotice(
        "Couldn't start the microphone — you can type instead, or try voice again.",
      ),
  });
  useEffect(() => {
    sessionEndRef.current = voiceModeApi.onSessionEnd;
  });
  // Reactive coach-playback flag for the voice-mode status line.
  const [coachPlaying, setCoachPlaying] = useState(false);
  useEffect(() => subscribePlayback(setCoachPlaying), []);

  /**
   * Wait for a native pack purchase to show up as replies.
   *
   * A consumable grants no entitlement, so the store's "purchased" says
   * nothing about our ledger — the credit arrives when the RevenueCat webhook
   * lands, usually within a second or two. Polling the SERVER is the only
   * honest way to know it has: the client must never add credits it has not
   * been told about.
   *
   * Bounded on purpose (AGENTS.md): a fixed number of attempts, then give up
   * and say so. The purchase is not lost when this times out — the webhook
   * still credits it, and the next screen load shows the balance.
   */
  const awaitPackCredit = useCallback(async (before: number) => {
    const DELAYS_MS = [800, 1200, 2000, 3000, 4000]; // ~11s, then stop asking
    for (const wait of DELAYS_MS) {
      await new Promise((resolve) => setTimeout(resolve, wait));
      try {
        const bal = await practiceApi.credits();
        if (bal.remainingReplies > before) {
          setCredits(bal);
          return true;
        }
      } catch {
        // A failed poll is not a failed purchase; keep trying the ones left.
      }
    }
    return false;
  }, []);

  const buyPack = useCallback(
    /**
     * `closeSheet` is called the moment the store is done with us, BEFORE any
     * branch that can raise a dialog.
     *
     * iOS will not present a second native modal window over one already up:
     * the dialog never appears and its invisible window eats every touch until
     * the screen unmounts. `notify` goes through the shared ConfirmDialog,
     * which is a Modal, and the pack sheet is a Modal — so every notify below
     * was a freeze waiting for a slow webhook. Device-observed twice now (the
     * group member menu on build 25, this on build 30).
     *
     * Closing here rather than in the caller's `finally` keeps the deliberate
     * part of the old order: the sheet stays up UNDER StoreKit's own payment
     * sheet, so the screen does not flash between tapping a price and paying.
     * By the time this runs the store is finished and the sheet has nothing
     * left to show.
     */
    async (pack: PracticePack, closeSheet: () => void = () => {}) => {
      const before = credits?.remainingReplies ?? 0;

      // Mock mode has no store and no Stripe: keep the instant grant the mock
      // API has always done, or packs become untestable off a real device.
      if (USE_MOCK) {
        await practiceApi.purchase(pack.id);
        await load();
        return;
      }

      const result = await getPaymentProvider().purchasePack(pack);

      // Before every branch below, because every one of them can end in a
      // dialog — cancelled is the only path that raises nothing, and closing
      // for it too costs nothing.
      closeSheet();

      if (result.status === "cancelled") return;

      if (result.status === "failed" || result.status === "unavailable") {
        notify(
          i18n.t("practice.errors.payment"),
          result.message ?? i18n.t("practice.tryAgainLater"),
        );
        return;
      }

      // On web `purchasePack` has navigated to Stripe and nothing below runs.
      // On native the store sheet has closed and the webhook is in flight.
      if (Platform.OS === "web") return;

      const credited = await awaitPackCredit(before);
      if (!credited) {
        // Deliberately not an error: the money moved and the replies are
        // coming. Saying "payment failed" here would be a lie that pushes the
        // buyer into paying twice.
        notify(
          i18n.t("practice.addRepliesTitle"),
          i18n.t("practice.packPending"),
        );
        await load();
      }
    },
    [awaitPackCredit, credits?.remainingReplies, load],
  );

  /**
   * A pack row's whole lifecycle: spin the row, run the purchase, close the
   * sheet. The store's own payment sheet appears OVER ours, so the user never
   * sees the practice screen flash between tapping a price and paying — the
   * gap that used to read as the tap having done nothing.
   */
  const buyFromSheet = useCallback(
    async (pack: PracticePack, rowLabel: string) => {
      setSheetBusyLabel(rowLabel);
      try {
        // The sheet closes inside buyPack, before anything can raise a dialog.
        // The `finally` below still runs and is harmless: closing an already
        // closed sheet is a no-op, and it remains the safety net for a throw.
        await buyPack(pack, () => setSheet(null));
      } finally {
        setSheetBusyLabel(null);
        setSheet(null);
      }
    },
    [buyPack],
  );

  const addReplies = useCallback(async () => {
    let packs: PracticePack[] = [];
    try {
      packs = await practiceApi.packs();
    } catch {
      notify(i18n.t("practice.practiceAlert"), i18n.t("practice.errors.packs"));
      return;
    }

    // Who is the authority on price here? On web our own numbers ARE what
    // Stripe charges. On native they are NOT: Apple rounds 8.50 to its nearest
    // tier and Play grosses up for local VAT, so the store has to be asked.
    const storePrices = USE_MOCK
      ? null
      : await getPaymentProvider().packPrices(packs);

    let options: { label: string; sublabel: string; onPress: () => void }[];

    if (storePrices === null) {
      // Symbol from the server-resolved region — the same resolution the charge
      // will use, so the sheet can't show £ and then bill $. Never rejects.
      const { currency } = await fetchBillingRegion();
      options = packs.map((p) => {
        const label = practicePackLabel(p);
        return {
          // Stable pinned price: the same number in the buyer's own currency
          // (€/£/$), matching exactly what Stripe charges — no FX conversion.
          label,
          sublabel: `${plusCurrencySymbol(currency)}${p.price.toFixed(2)}`,
          stayOpen: true,
          onPress: () => void buyFromSheet(p, label),
        };
      });
    } else {
      // Only packs the store actually quoted, at the store's own price. A pack
      // it would not quote is one it will not sell, and offering it at OUR
      // number would misstate the price at the moment of purchase.
      const priced = new Map(storePrices.map((q) => [q.id, q.price]));
      options = packs
        .filter((p) => priced.has(p.id))
        .map((p) => {
          const label = practicePackLabel(p);
          return {
            label,
            sublabel: priced.get(p.id) as string,
            stayOpen: true,
            onPress: () => void buyFromSheet(p, label),
          };
        });

      if (options.length === 0) {
        // Fail closed and say so, rather than opening an empty sheet.
        notify(
          i18n.t("practice.addRepliesTitle"),
          i18n.t("practice.errors.packsUnavailable"),
        );
        return;
      }
    }

    setSheet({
      title: i18n.t("practice.addRepliesTitle"),
      message: i18n.t("practice.addRepliesBody"),
      options,
    });
  }, [buyFromSheet]);

  const endWithSummary = useCallback(async () => {
    if (!activeSessionId || !session || summarizing) return;
    setSummarizing(true);
    try {
      const result = await practiceApi.summarize(activeSessionId);
      setSession(result.session);
      if (result.status === "ready") {
        setSummaryPanel({ status: "ready", summary: result.summary });
      } else if (result.status === "not_enough_practice") {
        setSummaryPanel({
          status: "not_enough_practice",
          reason: result.reason,
        });
      } else {
        setSummaryPanel({ status: "failed_retryable" });
      }
    } catch {
      notify(
        i18n.t("practice.practiceAlert"),
        i18n.t("practice.errors.summary"),
      );
    } finally {
      setSummarizing(false);
    }
  }, [activeSessionId, session, summarizing]);

  const endPractice = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      setSession(await practiceApi.end(activeSessionId));
    } catch {
      notify(i18n.t("practice.practiceAlert"), i18n.t("practice.errors.end"));
    }
  }, [activeSessionId]);

  const practiseAgain = useCallback(async () => {
    const scenarioId = session?.scenarioId;
    if (!scenarioId) return;
    try {
      setReferenceMessages(messages);
      setSummaryPanel(null);
      const fresh = await practiceApi.startSession(scenarioId);
      setSession(fresh.session);
      setPack(fresh.pack ?? null);
      setMessages(fresh.messages);
      setInitialModeChosen(fresh.messages.length > 0);
      setInput("");
      // A whole new session inside the same mounted screen: frame its card from
      // the top, and re-open the settle window, because none of this has laid
      // out yet.
      forceFocus("top");
    } catch {
      notify(i18n.t("practice.practiceAlert"), i18n.t("practice.errors.start"));
    }
  }, [messages, session?.scenarioId, forceFocus]);

  /** Reopen the just-ended session and return to practicing. */
  const continueSession = useCallback(async () => {
    if (!activeSessionId || hasSummary) return;
    try {
      setSession(await practiceApi.resume(activeSessionId));
      setSummaryPanel(null);
    } catch {
      notify(i18n.t("practice.practiceAlert"), i18n.t("practice.errors.reply"));
    }
  }, [activeSessionId, hasSummary]);

  const clearSession = useCallback(() => {
    setSheet({
      title: i18n.t("practice.clearTitle"),
      message: i18n.t("practice.clearBody"),
      options: [
        {
          get label() {
            return i18n.t("profile.edit.clear");
          },
          destructive: true,
          onPress: async () => {
            if (!activeSessionId) return;
            try {
              await practiceApi.clearSession(activeSessionId);
              router.replace("/practice");
            } catch {
              notify(
                i18n.t("practice.practiceAlert"),
                i18n.t("practice.errors.clear"),
              );
            }
          },
        },
      ],
    });
  }, [activeSessionId, router]);

  /** Change guidance level without resetting the scenario. */
  const changeMode = useCallback(
    async (mode: PracticeMode) => {
      if (!activeSessionId) return;
      if (session?.practiceMode === mode) {
        setInitialModeChosen(true);
        return;
      }
      try {
        setSession(await practiceApi.setMode(activeSessionId, mode));
        setInitialModeChosen(true);
      } catch {
        notify(i18n.t("practice.practiceAlert"), i18n.t("practice.errors.reply"));
      }
    },
    [activeSessionId, session?.practiceMode],
  );

  /** Menu → Balance: both pools in full, and when the plan pool refills. */
  const showBalance = useCallback(() => {
    if (!credits) return;
    notify(
      i18n.t("practice.balance.title"),
      i18n.t("practice.balance.body", {
        included: credits.includedRemaining,
        purchased: credits.purchasedRemaining,
        date: new Date(credits.billingPeriodEnd).toLocaleDateString(),
      }),
    );
  }, [credits]);

  const openModePicker = useCallback(() => {
    const current = session?.practiceMode ?? "guided";
    setSheet({
      title: i18n.t("practice.menu.mode"),
      variant: "picker",
      options: PRACTICE_MODES.map((m) => ({
        label: i18n.t(`practice.mode.${m}.label`),
        sublabel: i18n.t(`practice.mode.${m}.description`),
        selected: m === current,
        onPress: () => void changeMode(m),
      })),
    });
  }, [changeMode, session?.practiceMode]);

  const openVoicePicker = useCallback(() => {
    const currentVoice = practiceVoice;
    setSheet({
      title: i18n.t("practice.menu.voice"),
      variant: "picker",
      options: PRACTICE_VOICES.map((voice) => ({
        label: i18n.t(`practice.voice.${voice}`),
        selected: voice === currentVoice,
        onPress: () => setPracticeVoice(voice),
      })),
    });
  }, [practiceVoice]);

  /** Top-right Menu: Balance → Mode → Voice → End → Clear. */
  const openMenu = useCallback(() => {
    setSheet({
      title: i18n.t("practice.menu.title"),
      options: [
        {
          label: i18n.t("practice.menu.mode"),
          onPress: openModePicker,
          showChevron: true,
        },
        {
          label: i18n.t("practice.menu.voice"),
          onPress: openVoicePicker,
          showChevron: true,
        },
        { label: i18n.t("practice.end"), onPress: () => void endPractice() },
        {
          // What's left in each pool, but only after the user asks for it.
          label: i18n.t("practice.menu.balance"),
          onPress: showBalance,
          showChevron: true,
        },
        {
          label: i18n.t("practice.clearSession"),
          destructive: true,
          onPress: clearSession,
        },
      ],
    });
  }, [
    endPractice,
    clearSession,
    showBalance,
    openModePicker,
    openVoicePicker,
  ]);

  const guided = (session?.practiceMode ?? "guided") === "guided";

  // Prefer the session's stored pack (varied, localized); fall back to the
  // static i18n opener when the bank has no pack for this session.
  const opener = useMemo(() => {
    if (pack) {
      return {
        scene: pack.scene,
        goal: pack.goal,
        cue: pack.visibleSocialCue,
        starters: pack.starterOptions,
      };
    }
    const fallback = scenario ? guidedOpening(scenario) : null;
    return fallback
      ? { scene: fallback.scene, goal: fallback.goal, cue: "", starters: fallback.starters }
      : null;
  }, [pack, scenario]);

  /** Swap to a different stored setting (Guided). Free, no provider call. */
  const tryAnother = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      const res = await practiceApi.tryAnotherSetting(activeSessionId);
      setSession(res.session);
      setPack(res.pack ?? null);
      setInput("");
      // Frame the new scenario from the top. The card's own size change usually
      // triggers this via onContentSizeChange, but not when the replacement
      // happens to lay out at the same height — so ask explicitly rather than
      // leaving the user parked at the bottom of a scenario they haven't read.
      forceFocus("top");
    } catch (err) {
      const msg =
        err instanceof Error && /practice_rate_limited/.test(err.message)
          ? i18n.t("practice.ai.rateLimited")
          : i18n.t("practice.errors.reply");
      notify(i18n.t("practice.practiceAlert"), msg);
    }
  }, [activeSessionId, forceFocus]);

  /**
   * Switch this fresh curated session to the user's own scene. The session
   * becomes BYO-not-ready, so reloading drops us into the intake phase right
   * here in this inbox (same header, Menu, mic, composer).
   */
  const useOwnSituation = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      await practiceApi.useOwnScene(activeSessionId);
      await load();
    } catch {
      notify(i18n.t("practice.practiceAlert"), i18n.t("practice.byo.error"));
    }
  }, [activeSessionId, load]);

  // The mode picker shows regardless of credit balance: an out-of-replies (e.g.
  // free) user still gets to pick a role — a glimpse of the practice's shape —
  // rather than being locked out at the door. The scene opener, though, stays
  // behind the credit wall: once they've picked a role with no replies left they
  // see the "Add replies" gate, NOT the scenario.
  const showModeIntro =
    Boolean(scenario) &&
    !initialModeChosen &&
    messages.length === 0 &&
    session?.status === "active";

  const showOpener =
    !showModeIntro &&
    Boolean(opener && scenario) &&
    (messages.length > 0 ||
      (initialModeChosen && session?.status === "active" && remaining > 0));

  const listItems = useMemo<PracticeListItem[]>(() => {
    // BYO intake phase: the framing card + the recount conversation with the
    // setup guide. Guide bubbles carry an `intake:{session}:{i}` clip id so the
    // coach-voice button (and voice-mode auto-play) can fetch their audio from
    // the intake-audio endpoint — the index is the turn's position in the log.
    // The recount conversation with the setup guide. It stays in the transcript
    // for the whole BYO session — during intake AND after the hand-off — so the
    // scene the user built by talking is never discarded when the roleplay
    // begins. Guide bubbles keep their `intake:{session}:{i}` clip id.
    const sid = session?.id ?? id;
    const intakeBubbles: PracticeListItem[] =
      session?.sceneSource === "byo"
        ? intake.map((t, i) => ({
            id: t.role === "user" ? `intake_u_${i}` : intakeClipId(sid, i),
            role: t.role === "user" ? "user" : "coach",
            content: t.content,
            createdAt: "",
          }))
        : [];

    if (inIntake) {
      return [{ id: "byo_intro", kind: "byoIntro" }, ...intakeBubbles];
    }
    const items: PracticeListItem[] = [...referenceMessages];
    // BYO scenes are established conversationally in the recount above, so we
    // never inject a mode-intro or a generic scenario opener card here — that
    // would reset the screen and replace the user's own setup with a stock one.
    if (session?.sceneSource !== "byo") {
      if (showModeIntro && session) {
        items.push({ id: `mode_intro_${session.id}`, kind: "modeIntro" });
      } else if (showOpener && session) {
        items.push({ id: `opener_${session.id}`, kind: "opener" });
      }
    }
    // In voice mode a just-arrived coach reply is held out until its voice
    // plays (the thinking bubble stands in for it meanwhile).
    const shown = heldCoachId
      ? messages.filter((m) => m.id !== heldCoachId)
      : messages;
    const all = [...intakeBubbles, ...items, ...shown];
    if (summaryPanel) all.push({ id: "summary_panel", kind: "summary" });
    return all;
  }, [id, inIntake, intake, messages, heldCoachId, referenceMessages, session, showModeIntro, showOpener, summaryPanel]);

  /**
   * What the list renders. A session with a conversation is an INVERTED list —
   * newest item first, so the first painted frame is the newest line — while a
   * card-only session keeps reading order, because a scenario card is a
   * document: it is read from the top, and an inverted list would bottom-anchor
   * it and re-introduce the corrective jump the inversion removes elsewhere.
   * `hasConversation` flips at most once per session, forward, at the user's
   * own first line — measured in Chromium as a single-frame transition
   * coinciding with that send.
   */
  const listData = useMemo(
    () => (hasConversation ? [...listItems].reverse() : listItems),
    [hasConversation, listItems],
  );

  // MEASUREMENT. `saved` is the turns the server has given us, `drawn` is every
  // row the list renders (turns plus the scenario card and the feedback panel),
  // and `inv` records the ONE geometry flip a session makes. Unlike the
  // one-to-one thread there is no pending-to-saved swap here, so a turn is
  // expected to add rows; what this is for is placing the flip and the inserts
  // on the same timeline as `practice.sizeChange`.
  useEffect(() => {
    probe("practice.rows", {
      saved: messages.length,
      drawn: listData.length,
      inv: hasConversation ? 1 : 0,
    });
  }, [messages.length, listData.length, hasConversation]);
  // Committed geometry for scroll commands that outlive their closure — see
  // the ref's declaration. A LAYOUT effect on purpose: it runs synchronously
  // at commit, before the animation frame `refocusList` queues its scroll
  // into, so a command can never execute against the previous geometry.
  useLayoutEffect(() => {
    invertedRef.current = hasConversation;
  }, [hasConversation]);

  // The feedback is always the LAST item in reading order when present — what
  // `refocusList` frames the top of — which makes it item ZERO of an inverted
  // render. Clearing it (Practise again) re-arms the one-shot framing.
  useEffect(() => {
    summaryIndexRef.current = summaryPanel
      ? hasConversation
        ? 0
        : listItems.length - 1
      : -1;
    if (!summaryPanel) summaryFramedRef.current = false;
  });

  const renderModeIntroCard = () => (
    <View
      style={[
        styles.guidedCard,
        { backgroundColor: colors.surface, borderColor: colors.border },
      ]}
      accessibilityRole="summary"
    >
      <Text
        style={[styles.guidedLabel, { color: colors.textMuted }]}
        maxFontSizeMultiplier={1.35}
      >
        {i18n.t("practice.modeIntro.label")}
      </Text>
      <Text
        style={[styles.guidedScene, { color: colors.textBody }]}
        maxFontSizeMultiplier={1.6}
      >
        {i18n.t("practice.modeIntro.title")}
      </Text>
      <Text
        style={[styles.guidedGoal, { color: colors.textSecondary }]}
        maxFontSizeMultiplier={1.6}
      >
        {i18n.t("practice.modeIntro.body")}
      </Text>

      <View style={styles.modeOptions}>
        {PRACTICE_MODES.map((mode) => (
          <Pressable
            key={mode}
            onPress={() => void changeMode(mode)}
            accessibilityRole="button"
            accessibilityLabel={i18n.t("practice.modeIntro.chooseA11y", {
              mode: i18n.t(`practice.mode.${mode}.label`),
            })}
            style={({ pressed }) => [
              styles.modeOption,
              {
                backgroundColor: colors.surfaceSoft,
                borderColor: colors.border,
              },
              pressed && { opacity: 0.75 },
            ]}
          >
            <View style={styles.modeOptionCopy}>
              <Text
                style={[styles.modeOptionTitle, { color: colors.textStrong }]}
                maxFontSizeMultiplier={1.45}
              >
                {i18n.t(`practice.mode.${mode}.label`)}
              </Text>
              <Text
                style={[styles.modeOptionText, { color: colors.textSecondary }]}
                maxFontSizeMultiplier={1.45}
              >
                {i18n.t(`practice.mode.${mode}.description`)}
              </Text>
            </View>
            <Ionicons
              name="chevron-forward"
              size={18}
              color={colors.textMuted}
            />
          </Pressable>
        ))}
      </View>
    </View>
  );

  const renderOpenerCard = () => {
    if (!opener || !scenario) return null;

    return (
      <View
        style={[
          styles.guidedCard,
          { backgroundColor: colors.surface, borderColor: colors.border },
        ]}
        accessibilityRole="summary"
      >
        <Text
          style={[styles.guidedLabel, { color: colors.textMuted }]}
          maxFontSizeMultiplier={1.35}
        >
          {i18n.t("practice.guided.scenarioLabel")}
        </Text>
        <Text
          style={[styles.guidedScene, { color: colors.textBody }]}
          maxFontSizeMultiplier={1.6}
        >
          {opener.scene}
        </Text>
        {opener.cue ? (
          <Text
            style={[styles.guidedCue, { color: colors.textSecondary }]}
            maxFontSizeMultiplier={1.6}
          >
            {opener.cue}
          </Text>
        ) : null}
        {guided ? (
          <Text
            style={[styles.guidedGoal, { color: colors.textSecondary }]}
            maxFontSizeMultiplier={1.6}
          >
            {i18n.t("practice.guided.goalLabel")}: {opener.goal}
          </Text>
        ) : null}

        {guided && messages.length === 0 && !ended && remaining > 0 ? (
          <>
            <Text
              style={[styles.guidedTry, { color: colors.textBody }]}
              maxFontSizeMultiplier={1.6}
            >
              {i18n.t("practice.guided.tryOneOfThese")}
            </Text>
            {opener.starters.map((starter, i) => (
              <Pressable
                key={i}
                onPress={() => {
                  setInput(starter);
                  inputRef.current?.focus();
                }}
                accessibilityRole="button"
                accessibilityLabel={starter}
                style={({ pressed }) => [
                  styles.starterChip,
                  {
                    backgroundColor: colors.surfaceSoft,
                    borderColor: colors.border,
                  },
                  pressed && { opacity: 0.7 },
                ]}
              >
                <Text
                  style={[styles.starterText, { color: colors.textBody }]}
                  maxFontSizeMultiplier={1.45}
                >
                  {starter}
                </Text>
              </Pressable>
            ))}
            <Pressable
              onPress={() => inputRef.current?.focus()}
              accessibilityRole="button"
              style={styles.writeMyOwnBtn}
            >
              <Ionicons name="create-outline" size={16} color={colors.primary} />
              <Text
                style={[styles.writeMyOwnText, { color: colors.primary }]}
                maxFontSizeMultiplier={1.35}
              >
                {i18n.t("practice.guided.writeMyOwn")}
              </Text>
            </Pressable>
            <Text
              style={[styles.guidedHint, { color: colors.textMuted }]}
              maxFontSizeMultiplier={1.6}
            >
              {i18n.t("practice.guided.pickEditOrWrite")}
            </Text>
          </>
        ) : !guided && messages.length === 0 && !ended ? (
          <Text
            style={[styles.guidedHint, { color: colors.textMuted }]}
            maxFontSizeMultiplier={1.6}
          >
            {i18n.t(
              session?.practiceMode === "challenge"
                ? "practice.guided.challengeStart"
                : "practice.guided.roleplayStart",
            )}
          </Text>
        ) : null}

        {/* Swap the setting for a different stored one. Offered in EVERY mode —
            the setting is the scene itself, not a guided-only coaching aid, so
            roleplay and challenge get the same choice. Free and no provider
            call. Only before the first turn: swapping the scene afterwards
            would contradict the conversation already on screen. */}
        {pack && messages.length === 0 && !ended && remaining > 0 ? (
          <Pressable
            onPress={tryAnother}
            accessibilityRole="button"
            style={styles.writeMyOwnBtn}
          >
            <Ionicons name="shuffle-outline" size={16} color={colors.primary} />
            <Text
              style={[styles.writeMyOwnText, { color: colors.primary }]}
              maxFontSizeMultiplier={1.35}
            >
              {i18n.t("practice.guided.tryAnotherSetting")}
            </Text>
          </Pressable>
        ) : null}

        {/* Bring-your-own: swap this curated scene for a real moment of the
            user's own. Only before the roleplay has started (fresh session). */}
        {session?.sceneSource !== "byo" &&
        messages.length === 0 &&
        !ended &&
        remaining > 0 ? (
          <Pressable
            onPress={useOwnSituation}
            accessibilityRole="button"
            style={({ pressed }) => [
              styles.useOwnBtn,
              { borderColor: colors.primary },
              pressed && { opacity: 0.75 },
            ]}
          >
            <Ionicons name="create-outline" size={16} color={colors.primary} />
            <Text
              style={[styles.useOwnText, { color: colors.primary }]}
              maxFontSizeMultiplier={1.35}
            >
              {i18n.t("practice.byo.useOwn")}
            </Text>
          </Pressable>
        ) : null}
      </View>
    );
  };

  const renderByoIntroCard = () => (
    <View
      style={[
        styles.guidedCard,
        { backgroundColor: colors.surface, borderColor: colors.border },
      ]}
      accessibilityRole="summary"
    >
      <Text
        style={[styles.guidedLabel, { color: colors.textMuted }]}
        maxFontSizeMultiplier={1.35}
      >
        {i18n.t("practice.byo.title")}
      </Text>
      <Text
        style={[styles.guidedScene, { color: colors.textBody }]}
        maxFontSizeMultiplier={1.6}
      >
        {i18n.t("practice.byo.prompt")}
      </Text>
      <Text
        style={[styles.guidedGoal, { color: colors.textSecondary }]}
        maxFontSizeMultiplier={1.6}
      >
        {i18n.t("practice.byo.hint")}
      </Text>
      <Text
        style={[styles.guidedCue, { color: colors.textMuted }]}
        maxFontSizeMultiplier={1.6}
      >
        {i18n.t("practice.byo.example")}
      </Text>
    </View>
  );

  /**
   * Act on the single next step offered with a tip.
   *
   * Every action except `adjust_scene` ends with "now it's your turn", so they
   * all land in the composer — that is where the user's turn happens. Nothing
   * here sends a message on the user's behalf: a turn costs a reply, so pressing
   * a suggestion must never spend one silently.
   */
  const runSuggestedAction = useCallback(
    (action: PracticeSuggestedActionKind) => {
      if (action === "adjust_scene") {
        void tryAnother();
        return;
      }
      inputRef.current?.focus();
    },
    [tryAnother],
  );

  /**
   * A turn where opncue stepped OUT of the scene to answer the user.
   *
   * Deliberately not a chat bubble: help that looks like dialogue reads as the
   * character saying it, which is the exact confusion this feature exists to
   * remove. Card styling, opncue's own voice, and at most one next step.
   */
  const renderPracticeTip = (item: PracticeMessage) => {
    const action = item.response?.suggestedAction;
    return (
      <View
        style={[
          styles.tipCard,
          { backgroundColor: colors.surfaceSoft, borderColor: colors.primary },
        ]}
      >
        <View style={styles.tipHeader}>
          <Ionicons name="bulb-outline" size={15} color={colors.primary} />
          <Text
            style={[styles.tipLabel, { color: colors.primary }]}
            maxFontSizeMultiplier={1.35}
          >
            {i18n.t("practice.tip.label")}
          </Text>
        </View>

        <Text
          style={[styles.tipText, { color: colors.textBody }]}
          maxFontSizeMultiplier={1.6}
        >
          {item.content}
        </Text>

        {action ? (
          <Pressable
            onPress={() => runSuggestedAction(action.action)}
            accessibilityRole="button"
            accessibilityLabel={action.label}
            style={styles.writeMyOwnBtn}
          >
            <Ionicons name="arrow-forward" size={15} color={colors.primary} />
            <Text
              style={[styles.writeMyOwnText, { color: colors.primary }]}
              maxFontSizeMultiplier={1.35}
            >
              {action.label}
            </Text>
          </Pressable>
        ) : null}

        {!isOptimistic(item.id) ? (
          <CoachAudioButton messageId={item.id} voice={practiceVoice} />
        ) : null}
      </View>
    );
  };

  const renderMessage = ({ item }: { item: PracticeListItem }) => {
    if ("kind" in item) {
      if (item.kind === "modeIntro") return renderModeIntroCard();
      if (item.kind === "byoIntro") return renderByoIntroCard();
      if (item.kind === "summary") return renderSummaryPanel();
      return renderOpenerCard();
    }

    if (item.role === "system") {
      return (
        <View
          style={[
            styles.summaryCard,
            { backgroundColor: colors.surfaceSoft, borderColor: colors.border },
          ]}
        >
          <Text style={[styles.summaryText, { color: colors.textBody }]}>
            {item.content}
          </Text>
        </View>
      );
    }
    // opncue stepped out of the scene — render as help, not as the character.
    if (item.role === "coach" && item.response?.presentation === "practice_tip") {
      return renderPracticeTip(item);
    }

    const isUser = item.role === "user";
    return (
      <View
        style={[
          styles.bubbleRow,
          { justifyContent: isUser ? "flex-end" : "flex-start" },
        ]}
      >
        <View
          style={[
            styles.bubble,
            isUser
              ? { backgroundColor: colors.primary }
              : {
                  backgroundColor: colors.surface,
                  borderColor: colors.border,
                  borderWidth: 1,
                },
          ]}
        >
          <Text
            style={[
              styles.bubbleText,
              { color: isUser ? colors.primaryText : colors.textBody },
            ]}
          >
            {item.content}
          </Text>
          {/* Voice Practice (Phase B): play the AI coach voice. Self-hides
              unless the dev flag is on and the platform supports playback. */}
          {!isUser && !isOptimistic(item.id) ? (
            <CoachAudioButton messageId={item.id} voice={practiceVoice} />
          ) : null}
        </View>
      </View>
    );
  };

  const renderSummaryPanel = () => {
    if (!summaryPanel) return null;

    if (summaryPanel.status === "ready") {
      const summary = summaryPanel.summary;
      return (
        <View
          style={[
            styles.practiceSummaryCard,
            { backgroundColor: colors.surfaceSoft, borderColor: colors.border },
          ]}
        >
          <Text style={[styles.practiceSummaryTitle, { color: colors.textStrong }]}>
            {i18n.t("practice.summary.title")}
          </Text>
          <SummarySection
            title={i18n.t("practice.summary.mainTakeaway")}
            body={summary.mainTakeaway}
            colors={colors}
          />
          {summary.didWell.length > 0 ? (
            <View style={styles.practiceSummarySection}>
              <Text
                style={[styles.practiceSummaryLabel, { color: colors.textMuted }]}
              >
                {i18n.t("practice.summary.didWell")}
              </Text>
              {summary.didWell.map((item, index) => (
                <Text
                  key={`${index}-${item}`}
                  style={[styles.practiceSummaryBody, { color: colors.textBody }]}
                >
                  {item}
                </Text>
              ))}
            </View>
          ) : null}
          <SummarySection
            title={i18n.t("practice.summary.tryNext")}
            body={summary.tryNext.guidance}
            colors={colors}
          />
          {summary.tryNext.example ? (
            <SummarySection
              title={i18n.t("practice.summary.example")}
              body={summary.tryNext.example}
              colors={colors}
            />
          ) : null}
          {summary.tryNext.reason ? (
            <Text style={[styles.practiceSummaryBody, { color: colors.textBody }]}>
              {summary.tryNext.reason}
            </Text>
          ) : null}
          <SummarySection
            title={i18n.t("practice.summary.realLife")}
            body={summary.realLifeStep}
            colors={colors}
          />
          <View style={styles.summaryActionStack}>
            <Pressable
              onPress={practiseAgain}
              style={[styles.primaryBtn, { backgroundColor: colors.primary }]}
            >
              <Text
                style={[
                  styles.primaryBtnText,
                  { color: isDark ? colors.primaryText : colors.white },
                ]}
              >
                {i18n.t("practice.summary.actions.practiseAgain")}
              </Text>
            </Pressable>
            <Pressable
              onPress={() => router.replace("/practice")}
              style={[styles.summaryBtn, { borderColor: colors.border }]}
            >
              <Text style={[styles.summaryBtnText, { color: colors.textStrong }]}>
                {i18n.t("practice.summary.actions.anotherScenario")}
              </Text>
            </Pressable>
            <Pressable
              onPress={() => router.replace("/practice")}
              style={[styles.summaryBtn, { borderColor: colors.border }]}
            >
              <Text style={[styles.summaryBtnText, { color: colors.textStrong }]}>
                {i18n.t("practice.summary.actions.done")}
              </Text>
            </Pressable>
          </View>
        </View>
      );
    }

    const noPractice = summaryPanel.status === "not_enough_practice";
    const title = noPractice
      ? i18n.t(
          summaryPanel.reason === "no_practice"
            ? "practice.summary.noPractice.title"
            : "practice.summary.tooShort.title",
        )
      : i18n.t("practice.summary.failure.title");
    const body = noPractice
      ? i18n.t(
          summaryPanel.reason === "no_practice"
            ? "practice.summary.noPractice.body"
            : "practice.summary.tooShort.body",
        )
      : i18n.t("practice.summary.failure.body");
    return (
      <View
        style={[
          styles.practiceSummaryCard,
          { backgroundColor: colors.surfaceSoft, borderColor: colors.border },
        ]}
      >
        <Text style={[styles.practiceSummaryTitle, { color: colors.textStrong }]}>
          {title}
        </Text>
        <Text style={[styles.practiceSummaryBody, { color: colors.textBody }]}>
          {body}
        </Text>
        {!noPractice ? (
          <Text style={[styles.practiceSummaryMeta, { color: colors.textMuted }]}>
            {i18n.t("practice.summary.failure.integrity")}
          </Text>
        ) : null}
        <View style={styles.summaryActionStack}>
          {!noPractice ? (
            <Pressable
              onPress={endWithSummary}
              disabled={summarizing}
              style={[styles.primaryBtn, { backgroundColor: colors.primary }]}
            >
              {summarizing ? (
                <ActivityIndicator color={colors.white} size="small" />
              ) : (
                <Text
                  style={[
                    styles.primaryBtnText,
                    { color: isDark ? colors.primaryText : colors.white },
                  ]}
                >
                  {i18n.t("practice.summary.actions.retry")}
                </Text>
              )}
            </Pressable>
          ) : null}
          <Pressable
            onPress={() => {
              setSummaryPanel(null);
              inputRef.current?.focus();
            }}
            style={[styles.summaryBtn, { borderColor: colors.border }]}
          >
            <Text style={[styles.summaryBtnText, { color: colors.textStrong }]}>
              {i18n.t(
                noPractice
                  ? summaryPanel.reason === "no_practice"
                    ? "practice.summary.actions.continuePractice"
                    : "practice.summary.actions.keepPractising"
                  : "practice.summary.actions.return",
              )}
            </Text>
          </Pressable>
        </View>
      </View>
    );
  };

  // The two edges of the transcript, placed by GEOMETRY rather than by prop
  // name: in an inverted list the FOOTER renders at the visual top and the
  // HEADER at the visual bottom, so what belongs where swaps with the flip.
  // The live edge — the voice interim bubble and the thinking bubble — belongs
  // at the visual bottom, with the newest line; the loading-earlier row at the
  // visual top, where the fetched page will appear.
  const loadingOlderRow = loadingOlder ? (
    <View style={styles.loadingOlder}>
      <Text style={styles.loadingOlderText}>
        {i18n.t("social.chat.loadingEarlier")}
      </Text>
    </View>
  ) : null;
  const liveEdgeRows = (
    <>
      {voiceInterim.trim() && (voice.listening || voice.pendingSend) ? (
        <View
          style={[
            styles.bubbleRow,
            { justifyContent: "flex-end" },
          ]}
        >
          <View
            style={[
              styles.bubble,
              styles.voiceGhostBubble,
              { backgroundColor: colors.primary },
            ]}
          >
            <Text
              style={[styles.bubbleText, { color: colors.primaryText }]}
            >
              {voiceInterim}
            </Text>
          </View>
        </View>
      ) : null}
      {sending || heldCoachId ? (
        <View
          style={[
            styles.bubbleRow,
            styles.thinkingRow,
            { justifyContent: "flex-start" },
          ]}
        >
          <View
            style={[
              styles.bubble,
              styles.thinkingBubble,
              {
                backgroundColor: colors.surface,
                borderColor: colors.border,
              },
            ]}
          >
            <ActivityIndicator size="small" color={colors.primary} />
            <Text
              style={[
                styles.bubbleText,
                { color: colors.textSecondary },
              ]}
            >
              {i18n.t("practice.ai.thinking")}
            </Text>
          </View>
        </View>
      ) : null}
    </>
  );

  return (
    <SafeAreaView
      style={[styles.screen, { backgroundColor: colors.background }]}
    >
      <StatusBar style={colors.background === "#0D1724" ? "light" : "dark"} />

      {/* Header — clearly labeled practice, never a human profile */}
      <View
        style={[
          styles.header,
          Platform.OS === "web" && styles.headerWeb,
          Platform.OS === "web" && { backgroundColor: colors.surface },
          { borderBottomColor: colors.border },
        ]}
      >
        <Pressable onPress={goBack} hitSlop={12} style={styles.iconBtn}>
          <Ionicons name="chevron-back" size={26} color={colors.textStrong} />
        </Pressable>
        <View style={styles.headerCopy}>
          <Text
            maxFontSizeMultiplier={1.5}
            numberOfLines={2}
            style={[styles.headerTitle, { color: colors.textStrong }]}
          >
            {i18n.t("practice.title")}
          </Text>
          {scenario && (
            <Text
              maxFontSizeMultiplier={1.35}
              numberOfLines={2}
              style={[styles.headerSub, { color: colors.textSecondary }]}
            >
              {practiceScenarioCopy(scenario).title}
            </Text>
          )}
        </View>
        {!ended && (
          <Pressable
            onPress={openMenu}
            hitSlop={12}
            style={styles.endBtn}
            accessibilityRole="button"
            accessibilityLabel={i18n.t("practice.menu.title")}
          >
            <Text
              maxFontSizeMultiplier={1.3}
              numberOfLines={2}
              style={[styles.endBtnText, { color: colors.primary }]}
            >
              {i18n.t("practice.menu.title")}
            </Text>
          </Pressable>
        )}
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={colors.primary} />
        </View>
      ) : (
        <KeyboardAvoidingContainer
          style={{ flex: 1 }}
          keyboardVerticalOffset={8}
        >
          <FlatList
            ref={listRef}
            style={styles.listFrame}
            // Inverted only once there is a conversation: the newest line
            // becomes item zero and the FIRST painted frame shows it, where
            // the old design scrolled there after layout and flashed the top.
            // A card-only session stays in reading order — a scenario card is
            // read from the top, and inverting it would bottom-anchor the
            // card and re-introduce the corrective jump.
            inverted={hasConversation}
            data={listData}
            keyExtractor={(m) => m.id}
            renderItem={renderMessage}
            contentContainerStyle={
              // The 72px composer clearance must sit at the VISUAL bottom,
              // which is the container's start once inverted.
              hasConversation ? styles.listInverted : styles.list
            }
            showsVerticalScrollIndicator={false}
            onScroll={onScroll}
            scrollEventThrottle={16}
            onContentSizeChange={onContentSizeChange}
            onLayout={() => refocusList()}
            // Non-inverted, an older page PREPENDS and shifts everything below
            // it; inverted, a new line inserts at the scroll start and shifts a
            // reader who is anywhere else. Native absorbs both through this;
            // the web build relies on browser scroll anchoring instead
            // (measured holding the reader's row to the pixel).
            //
            // Anchor only a reader who has SCROLLED AWAY, never a follower —
            // the same rule, for the same reason, as `use-thread-follow.ts`.
            // Held on unconditionally, native anchors the previously-newest row
            // when a line arrives and shifts the offset by the new row's
            // height; that frame paints before `onContentSizeChange` can
            // correct it, so the sender watches their bubble land low and then
            // settle. It is the exact jump the one-to-one and group threads
            // fixed by making this conditional, and practice was the thread
            // left behind — reported on build 16 for both the typed line and
            // the reply. A follower gives up nothing: at offset 0 an inverted
            // list needs no scroll at all for an insert at index zero.
            maintainVisibleContentPosition={
              following ? null : { minIndexForVisible: 0 }
            }
            // The older end: the scroll end once inverted, and while the list
            // is still card-only `hasAnchor` inside the guard makes this inert.
            onEndReached={maybeLoadOlder}
            onEndReachedThreshold={0.5}
            ListHeaderComponent={hasConversation ? liveEdgeRows : loadingOlderRow}
            // scrollToIndex throws if the target hasn't been measured yet. The
            // settle pass in `refocusList` normally covers that, but a summary
            // taller than the render window can still lose the race — so fall
            // back to the offset the list itself computed rather than leaving
            // the user wherever they happened to be.
            onScrollToIndexFailed={({ averageItemLength, index }) => {
              listRef.current?.scrollToOffset({
                offset: averageItemLength * index,
                animated: false,
              });
            }}
            ListFooterComponent={hasConversation ? loadingOlderRow : liveEdgeRows}
          />

          {/* Retryable failure — same requestId, so no duplicate/no re-charge. */}
          {retryTurn && !sending ? (
            <View style={[styles.banner, Platform.OS === "web" && styles.bannerWeb]}>
              <View style={styles.bannerRow}>
                <Text
                  maxFontSizeMultiplier={1.45}
                  style={[styles.bannerText, { color: colors.textBody }]}
                >
                  {i18n.t("practice.ai.errorTemporary")}
                </Text>
                <Pressable onPress={() => send({ ...retryTurn, retry: true })}>
                  <Text
                    maxFontSizeMultiplier={1.3}
                    numberOfLines={2}
                    style={[styles.bannerLink, { color: colors.primary }]}
                  >
                    {i18n.t("practice.ai.retry")}
                  </Text>
                </Pressable>
              </View>
            </View>
          ) : null}

          {/* Stopped confirmation — the user cancelled the last reply. */}
          {stopped && !sending && !retryTurn ? (
            <View style={[styles.banner, Platform.OS === "web" && styles.bannerWeb]}>
              <Text
                maxFontSizeMultiplier={1.45}
                style={[styles.bannerText, { color: colors.textMuted }]}
              >
                {i18n.t("practice.ai.stopped")}
              </Text>
            </View>
          ) : null}

          {/* Low / zero credit banner — intake and roleplay both cost replies.
              Held back until a mode is chosen, so an out-of-replies user still
              sees the mode picker + scene first, not a paywall at the door. */}
          {!ended && remaining <= 5 && !showModeIntro && (
            <View
              style={[styles.banner, Platform.OS === "web" && styles.bannerWeb]}
            >
              {remaining <= 0 ? (
                <>
                  <Text
                    maxFontSizeMultiplier={1.45}
                    style={[styles.bannerText, { color: colors.textBody }]}
                  >
                    {i18n.t("practice.outOfReplies")}
                  </Text>
                  <View style={styles.bannerActions}>
                    <Pressable
                      onPress={addReplies}
                      style={[
                        styles.bannerBtn,
                        { backgroundColor: colors.primary },
                        // Alone (no End & summarize), keep its natural size
                        // instead of stretching across the row.
                        messages.length === 0 && styles.bannerBtnSolo,
                      ]}
                    >
                      <Text
                        maxFontSizeMultiplier={1.3}
                        numberOfLines={2}
                        style={[styles.bannerBtnText, { color: colors.white }]}
                      >
                        {i18n.t("practice.addReplies")}
                      </Text>
                    </Pressable>
                    {/* End & summarize only makes sense once there's a
                        conversation. A fresh out-of-replies session (e.g. a free
                        user) sees only Add replies. */}
                    {messages.length > 0 && (
                      <Pressable
                        onPress={endWithSummary}
                        style={[
                          styles.bannerBtnGhost,
                          {
                            backgroundColor: colors.primarySoft,
                            borderColor: colors.primary,
                          },
                        ]}
                      >
                        <Text
                          maxFontSizeMultiplier={1.3}
                          numberOfLines={2}
                          style={[
                            styles.bannerBtnGhostText,
                            { color: colors.primaryStrong },
                          ]}
                        >
                          {i18n.t("practice.endSummarize")}
                        </Text>
                      </Pressable>
                    )}
                  </View>
                </>
              ) : (
                <View style={styles.bannerRow}>
                  <Text
                    maxFontSizeMultiplier={1.45}
                    style={[styles.bannerText, { color: colors.textBody }]}
                  >
                    {i18n.t("practice.remaining", { count: remaining })}
                  </Text>
                  <Pressable onPress={addReplies}>
                    <Text
                      maxFontSizeMultiplier={1.3}
                      numberOfLines={2}
                      style={[styles.bannerLink, { color: colors.primary }]}
                    >
                      {i18n.t("practice.addMore")}
                    </Text>
                  </Pressable>
                </View>
              )}
            </View>
          )}

          {/* Composer, or ended-state actions.
              Before a summary exists the ended bar offers Get Summary / Continue
              / Finish. Once a summary is on screen it carries its own actions
              (Practise again / Try another / Done), so the bar is dropped — no
              redundant "Start New Practice" on the summary page. */}
          {ended ? (
            !hasSummary ? (
              <View style={[styles.endedBar, { borderTopColor: colors.border }]}>
                <Pressable
                  onPress={endWithSummary}
                  disabled={summarizing}
                  style={[styles.primaryBtn, { backgroundColor: colors.primary }]}
                >
                  {summarizing ? (
                    <ActivityIndicator
                      color={isDark ? colors.primaryText : colors.white}
                      size="small"
                    />
                  ) : (
                    <Text
                      style={[
                        styles.primaryBtnText,
                        { color: isDark ? colors.primaryText : colors.white },
                      ]}
                    >
                      {i18n.t("practice.getSummary")}
                    </Text>
                  )}
                </Pressable>
                <Pressable
                  onPress={continueSession}
                  accessibilityRole="button"
                  style={[styles.summaryBtn, { borderColor: colors.border }]}
                >
                  <Text
                    style={[styles.summaryBtnText, { color: colors.textStrong }]}
                  >
                    {i18n.t("practice.continue")}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => router.replace("/practice")}
                  accessibilityRole="button"
                  style={[styles.summaryBtn, { borderColor: colors.border }]}
                >
                  <Text
                    style={[styles.summaryBtnText, { color: colors.textStrong }]}
                  >
                    {i18n.t("practice.finish")}
                  </Text>
                </Pressable>
              </View>
            ) : null
          ) : (
            <View
              ref={composerRef}
              style={[
                styles.composer,
                Platform.OS === "web" && styles.composerWeb,
                {
                  borderTopColor:
                    Platform.OS === "web" ? "transparent" : colors.border,
                  backgroundColor:
                    Platform.OS === "web" ? "transparent" : colors.surface,
                },
              ]}
            >
              {voice.supported ? (
                <View style={styles.voiceRow}>
                  <Text
                    style={[
                      styles.voiceDisclosure,
                      {
                        // Green the moment capture is truly live — the state
                        // change the user is waiting on deserves a colour
                        // change, not just a verb swap (owner, 2026-09-03).
                        color:
                          voiceMode && !voice.notice
                            ? voice.listening
                              ? colors.success
                              : colors.primary
                            : colors.textMuted,
                      },
                    ]}
                    maxFontSizeMultiplier={1.3}
                  >
                    {voice.notice
                      ? voice.notice
                      : !voiceMode
                        ? "Tap the mic for hands-free voice"
                        : voice.preparing
                          ? // One phrase for the whole preparing phase (owner,
                            // 2026-09-03): the percentage read as clutter here.
                            // voice.preparingProgress still carries the number
                            // if a quieter surface ever wants it.
                            "Voice mode · getting the mic ready…"
                          : voice.listening
                          ? "Voice mode · listening, speak freely"
                          : voice.pendingSend
                            ? "Voice mode · got it, sending…"
                            : coachPlaying
                              ? "Voice mode · coach is speaking…"
                              : sending || heldCoachId
                                ? "Voice mode · coach is thinking…"
                                : "Voice mode on · tap the mic to stop"}
                  </Text>
                  {voiceMode ? (
                    // The meter is the one element that PROVES you are being
                    // heard, and it used to appear only once capture was live —
                    // so the moment the user most needed a signal was the
                    // moment nothing was drawn. The track is now present while
                    // preparing, empty and dimmed, and filling is the "go".
                    <View
                      style={[
                        styles.voiceMeterTrack,
                        !voice.listening && styles.voiceMeterTrackIdle,
                      ]}
                    >
                      {voice.listening ? (
                        <View
                          style={[
                            styles.voiceMeterFill,
                            {
                              backgroundColor: colors.primary,
                              width: `${Math.min(100, Math.round(voice.level * 600))}%`,
                            },
                          ]}
                        />
                      ) : null}
                    </View>
                  ) : null}
                </View>
              ) : null}
              <View style={styles.composerRow}>
                <TextInput
                  ref={inputRef}
                  value={input}
                  onChangeText={(t) => {
                    setInput(t);
                    if (stopped) setStopped(false);
                    voice.cancelPending();
                    // Emptied the box (deleted all / cleared) → let voice resume.
                    if (!t.trim()) endTyping();
                  }}
                  // A real keystroke (never a programmatic voice fill) means the
                  // user is typing this turn — pause the mic so it doesn't capture
                  // keyboard clatter or overwrite what they're typing.
                  onKeyPress={noteTyping}
                  onFocus={voice.cancelPending}
                  placeholder={
                    inIntake
                      ? i18n.t("practice.byo.placeholder")
                      : // On the mode picker, "Out of replies" is held back the
                        // same way the credit banner is: an out-of-replies user
                        // who never taps a mode would otherwise meet the verdict
                        // with no route to the pack sheet, which only appears
                        // once a mode is chosen. The input, mic and send button
                        // all gate on `remaining` separately, so nothing here
                        // becomes sendable — only the label waits.
                        remaining <= 0 && !showModeIntro
                        ? i18n.t("practice.outOfRepliesPlaceholder")
                        : i18n.t("practice.replyPlaceholder")
                  }
                  placeholderTextColor={colors.textMuted}
                  // Stay editable while sending so the keyboard doesn't dismiss
                  // after each message (the send button handles the busy state).
                  // Intake bills like the roleplay, so the credit gate is uniform.
                  editable={remaining > 0}
                  multiline
                  style={[
                    styles.textInput,
                    {
                      backgroundColor: colors.surface,
                      color: colors.text,
                      borderColor:
                        Platform.OS === "web" || !isDark
                          ? colors.primary
                          : colors.border,
                    },
                  ]}
                />
                {voice.supported ? (
                  <Pressable
                    // Toggles hands-free VOICE MODE on/off (stays on until off).
                    // Clearing the interim here rather than in an effect: going
                    // off drops any ghost bubble, going on starts clean, and
                    // neither costs a render on every STT partial.
                    onPress={() => {
                      setVoiceMode((on) => !on);
                      setVoiceInterim("");
                    }}
                    accessibilityRole="switch"
                    accessibilityState={{ checked: voiceMode }}
                    accessibilityLabel={
                      voiceMode ? "Turn off voice mode" : "Turn on voice mode"
                    }
                    disabled={remaining <= 0}
                    style={[
                      styles.micBtn,
                      {
                        // Three states, not two. The button used to reach its
                        // full "live" fill the instant voice mode turned on,
                        // while capture was still starting — so the loudest
                        // element on screen said LIVE and the quietest said
                        // "getting the mic ready…". People spoke into the gap
                        // and had to repeat themselves, which is exactly what
                        // they were reading the interface correctly to expect.
                        //
                        // Preparing now looks like preparing on BOTH platforms:
                        // native pays a permission check, an idle poll and an
                        // OS recogniser bind; web can be waiting on the whisper
                        // model, the worker and the audio graph. Different
                        // causes, same lie.
                        backgroundColor: voiceMode
                          ? voice.listening
                            ? colors.primary
                            : colors.surfaceSoft
                          : colors.surfaceSoft,
                        borderColor:
                          voiceMode && !voice.listening
                            ? colors.primary
                            : colors.border,
                      },
                      voiceMode && !voice.listening && styles.micBtnPreparing,
                      remaining <= 0 && styles.sendBtnDisabled,
                    ]}
                  >
                    <Ionicons
                      name={voiceMode ? "stop" : "mic-outline"}
                      size={20}
                      color={
                        voiceMode && voice.listening
                          ? colors.white
                          : colors.primary
                      }
                    />
                  </Pressable>
                ) : null}
                <Pressable
                  // Roleplay: while a reply generates this is a STOP button.
                  // Intake: no stop (calls are quick) — just a busy spinner.
                  onPress={
                    sending
                      ? inIntake
                        ? undefined
                        : stop
                      : inIntake
                        ? () => sendIntake()
                        : () => send()
                  }
                  accessibilityRole="button"
                  accessibilityLabel={
                    sending && !inIntake
                      ? i18n.t("practice.ai.stop")
                      : i18n.t("practice.ai.send")
                  }
                  disabled={
                    sending ? inIntake : !input.trim() || remaining <= 0
                  }
                  style={[
                    styles.sendBtn,
                    {
                      backgroundColor: colors.primary,
                    },
                    !sending &&
                      (!input.trim() || remaining <= 0) &&
                      styles.sendBtnDisabled,
                  ]}
                >
                  {sending ? (
                    inIntake ? (
                      <ActivityIndicator size="small" color={colors.primaryText} />
                    ) : (
                      <Ionicons
                        name="stop"
                        size={18}
                        color={colors.primaryText}
                      />
                    )
                  ) : (
                    <Ionicons
                      name="arrow-up"
                      size={20}
                      color={colors.primaryText}
                    />
                  )}
                </Pressable>
              </View>
            </View>
          )}
        </KeyboardAvoidingContainer>
      )}

      {/*
        Cross-platform choice sheet. Replaces multi-button Alert.alert, which is
        a no-op on web and silently broke End / Clear / Add Replies there.
      */}
      <Modal
        visible={sheet !== null}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => {
          if (!sheetBusyLabel) setSheet(null);
        }}
      >
        <View style={styles.sheetScreen}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={i18n.t("practice.dismissA11y")}
            style={styles.sheetBackdrop}
            onPress={() => {
              if (!sheetBusyLabel) setSheet(null);
            }}
          />
          {sheet && (
            <View
              style={[
                styles.sheet,
                { backgroundColor: colors.surface, borderColor: colors.border },
              ]}
            >
              <Text style={[styles.sheetTitle, { color: colors.textStrong }]}>
                {sheet.title}
              </Text>
              {sheet.message && (
                <Text
                  style={[styles.sheetMessage, { color: colors.textSecondary }]}
                >
                  {sheet.message}
                </Text>
              )}
              <View style={styles.sheetOptions}>
                {sheet.options.map((opt) => {
                  const rowBusy = sheetBusyLabel === opt.label;
                  const picker = sheet.variant === "picker";
                  return (
                    <Pressable
                      key={opt.label}
                      accessibilityState={{
                        busy: rowBusy,
                        disabled: sheetBusyLabel !== null,
                        selected: picker ? opt.selected : undefined,
                      }}
                      onPress={() => {
                        // One slow row at a time; taps elsewhere are inert
                        // rather than dismissing a purchase mid-flight.
                        if (sheetBusyLabel) return;
                        if (!opt.stayOpen) setSheet(null);
                        opt.onPress?.();
                      }}
                      style={[
                        styles.sheetOption,
                        {
                          borderColor:
                            picker && opt.selected
                              ? colors.primary
                              : colors.border,
                          backgroundColor: picker
                            ? colors.surfaceSoft
                            : "transparent",
                        },
                      ]}
                    >
                      <View style={styles.sheetOptionCopy}>
                        <Text
                          style={[
                            styles.sheetOptionLabel,
                            {
                              color: opt.destructive
                                ? colors.danger
                                : picker && opt.selected
                                  ? colors.primary
                                  : colors.textStrong,
                            },
                          ]}
                        >
                          {opt.label}
                        </Text>
                        {opt.sublabel && (
                          <Text
                            style={[
                              styles.sheetOptionSub,
                              { color: colors.textSecondary },
                            ]}
                          >
                            {opt.sublabel}
                          </Text>
                        )}
                      </View>
                      {rowBusy ? (
                        <ActivityIndicator size="small" color={colors.primary} />
                      ) : picker && opt.selected ? (
                        <Ionicons
                          name="checkmark"
                          size={18}
                          color={colors.primary}
                        />
                      ) : null}
                      {!picker &&
                      opt.onPress &&
                      !rowBusy &&
                      (opt.showChevron || !opt.sublabel) ? (
                        <Ionicons
                          name="chevron-forward"
                          size={18}
                          color={colors.textMuted}
                        />
                      ) : null}
                    </Pressable>
                  );
                })}
                <Pressable
                  onPress={() => {
                    if (!sheetBusyLabel) setSheet(null);
                  }}
                  style={styles.sheetCancel}
                >
                  <Text
                    style={[
                      styles.sheetCancelText,
                      { color: colors.textMuted },
                    ]}
                  >
                    {i18n.t("common.cancel")}
                  </Text>
                </Pressable>
              </View>
            </View>
          )}
        </View>
      </Modal>
    </SafeAreaView>
  );
}

function SummarySection({
  title,
  body,
  colors,
}: {
  title: string;
  body: string;
  colors: ReturnType<typeof useAppTheme>["colors"];
}) {
  if (!body.trim()) return null;
  return (
    <View style={styles.practiceSummarySection}>
      <Text style={[styles.practiceSummaryLabel, { color: colors.textMuted }]}>
        {title}
      </Text>
      <Text style={[styles.practiceSummaryBody, { color: colors.textBody }]}>
        {body}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  loadingOlder: {
    paddingVertical: 8,
    alignItems: "center",
  },
  loadingOlderText: {
    fontSize: 12,
    opacity: 0.6,
  },
  screen: { flex: 1 },
  header: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  headerWeb: {
    maxWidth: "100%",
    alignSelf: "stretch",
    minHeight: 62,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  iconBtn: {
    width: 34,
    height: 34,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  headerCopy: { flex: 1, minWidth: 0 },
  headerTitle: { fontSize: 16, fontWeight: "700", lineHeight: 21 },
  headerSub: { fontSize: 12, lineHeight: 16, marginTop: 1 },
  endBtn: {
    maxWidth: "36%",
    flexShrink: 0,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  endBtnText: {
    fontSize: 15,
    lineHeight: 20,
    fontWeight: "600",
    textAlign: "right",
  },
  center: { flex: 1, alignItems: "center", justifyContent: "center" },
  listFrame: {
    flex: 1,
  },
  list: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    padding: 16,
    paddingBottom: 72,
    gap: 8,
    flexGrow: 1,
  },
  // The same container once inverted: the transform flips which edge each
  // padding lands on, so the 72px composer clearance moves to `paddingTop` to
  // stay at the VISUAL bottom.
  listInverted: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    padding: 16,
    paddingTop: 72,
    gap: 8,
    flexGrow: 1,
  },
  bubbleRow: { minWidth: 0, flexDirection: "row" },
  bubble: {
    maxWidth: "82%",
    minWidth: 0,
    paddingHorizontal: 13,
    paddingVertical: 9,
    borderRadius: 16,
  },
  bubbleText: { minWidth: 0, fontSize: 15, lineHeight: 21 },
  thinkingBubble: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderWidth: 1,
  },
  // Transient "listening" bubble: a ghosted user turn that solidifies on send.
  voiceGhostBubble: {
    opacity: 0.6,
  },
  thinkingRow: {
    marginBottom: 5,
  },
  guidedCard: {
    minWidth: 0,
    gap: 6,
    padding: 14,
    marginBottom: 12,
    borderRadius: 14,
    borderWidth: 1,
  },
  // Practice tip: full-width card rather than a side-anchored bubble, so it
  // reads as opncue talking to the user rather than the character talking.
  tipCard: {
    minWidth: 0,
    gap: 8,
    padding: 14,
    marginBottom: 12,
    borderRadius: 14,
    borderWidth: 1,
  },
  tipHeader: { flexDirection: "row", alignItems: "center", gap: 6 },
  tipLabel: {
    fontSize: 11,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  tipText: { minWidth: 0, fontSize: 15, lineHeight: 21 },
  guidedLabel: {
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  guidedScene: { fontSize: 15, lineHeight: 21 },
  guidedCue: { fontSize: 14, lineHeight: 20, fontStyle: "italic" },
  guidedGoal: { fontSize: 14, lineHeight: 20 },
  guidedTry: { fontSize: 14, fontWeight: "600", marginTop: 6 },
  modeOptions: {
    gap: 8,
    marginTop: 4,
  },
  modeOption: {
    minWidth: 0,
    minHeight: 58,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
  },
  modeOptionCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  modeOptionTitle: {
    fontSize: 15,
    lineHeight: 20,
    fontWeight: "700",
  },
  modeOptionText: {
    minWidth: 0,
    fontSize: 13,
    lineHeight: 18,
  },
  starterChip: {
    maxWidth: "100%",
    minHeight: 44,
    justifyContent: "center",
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  starterText: { minWidth: 0, fontSize: 15, lineHeight: 20 },
  writeMyOwnBtn: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
    minHeight: 44,
    marginTop: 2,
  },
  writeMyOwnText: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "600",
  },
  guidedHint: { fontSize: 12, lineHeight: 17, marginTop: 2 },
  useOwnBtn: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    minHeight: 44,
    marginTop: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
  },
  useOwnText: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "700",
  },
  summaryCard: { padding: 14, borderRadius: 14, borderWidth: 1 },
  summaryText: { fontSize: 14, lineHeight: 21 },
  practiceSummaryCard: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    gap: 12,
  },
  practiceSummaryTitle: { fontSize: 20, lineHeight: 26, fontWeight: "800" },
  practiceSummarySection: { gap: 5 },
  practiceSummaryLabel: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  practiceSummaryBody: { fontSize: 14, lineHeight: 21 },
  practiceSummaryMeta: { fontSize: 12, lineHeight: 17 },
  summaryActionStack: { gap: 8, marginTop: 2 },
  banner: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    marginBottom: 4,
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 6,
    gap: 8,
  },
  bannerWeb: {
    paddingTop: 0,
  },
  bannerRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  bannerText: { flex: 1, minWidth: 0, fontSize: 13, lineHeight: 18 },
  bannerLink: { fontSize: 13, lineHeight: 18, fontWeight: "700" },
  bannerActions: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  bannerBtnSolo: { flexGrow: 0, alignSelf: "flex-start" },
  bannerBtn: {
    minHeight: 44,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 150,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 10,
  },
  bannerBtnText: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "700",
    textAlign: "center",
  },
  bannerBtnGhost: {
    minHeight: 44,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 150,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 10,
    borderWidth: 1,
  },
  bannerBtnGhostText: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "600",
    textAlign: "center",
  },
  composer: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    borderTopWidth: 1,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 12,
    gap: 6,
  },
  composerWeb: {
    paddingTop: 8,
  },
  composerRow: {
    minWidth: 0,
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 8,
  },
  textInput: {
    flex: 1,
    minWidth: 0,
    minHeight: 42,
    maxHeight: 120,
    borderRadius: 20,
    borderWidth: 1.5,
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 10,
    fontSize: 15,
  },
  sendBtn: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: "center",
    justifyContent: "center",
  },
  micBtn: {
    width: 42,
    height: 42,
    borderRadius: 21,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  voiceRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    paddingHorizontal: 4,
    marginBottom: 4,
  },
  voiceDisclosure: {
    flexShrink: 1,
    // 13/18 up from 11/15: this line is the ONLY narration of a state
    // machine the user is actively waiting on (downloading -> mic ready ->
    // listening), and 11px made the answer to "is anything happening?"
    // easy to miss (owner, 2026-09-03).
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "600",
  },
  voiceMeterTrack: {
    width: 44,
    height: 4,
    borderRadius: 2,
    overflow: "hidden",
    backgroundColor: "rgba(127,127,127,0.25)",
  },
  /** Reserved and visibly inert: "we are not capturing yet", not "no signal". */
  voiceMeterTrackIdle: {
    opacity: 0.4,
  },
  voiceMeterFill: {
    height: "100%",
    borderRadius: 2,
  },
  /** Outlined, not filled — voice mode is ON but the mic is not open yet. */
  micBtnPreparing: {
    borderWidth: 1,
  },
  sendBtnDisabled: { opacity: 0.45 },
  endedBar: {
    borderTopWidth: StyleSheet.hairlineWidth,
    padding: 14,
    gap: 10,
    alignItems: "center",
  },
  primaryBtn: {
    width: "100%",
    paddingVertical: 14,
    borderRadius: 14,
    alignItems: "center",
  },
  primaryBtnText: { fontSize: 15, fontWeight: "700" },
  summaryBtn: {
    width: "100%",
    minHeight: 48,
    paddingVertical: 13,
    borderRadius: 14,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  summaryBtnText: { fontSize: 15, fontWeight: "600" },
  sheetScreen: { flex: 1, justifyContent: "flex-end" },
  sheetBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(8, 15, 26, 0.55)",
  },
  sheet: {
    padding: 18,
    paddingBottom: 28,
    gap: 6,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderWidth: 1,
  },
  sheetTitle: { fontSize: 17, fontWeight: "700" },
  sheetMessage: { fontSize: 13, lineHeight: 18 },
  sheetOptions: { marginTop: 10, gap: 10 },
  sheetOption: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 52,
    paddingHorizontal: 16,
    borderRadius: 14,
    borderWidth: 1,
  },
  sheetOptionCopy: { flex: 1, gap: 3 },
  sheetOptionLabel: { fontSize: 15, fontWeight: "600" },
  sheetOptionSub: { fontSize: 14, fontWeight: "600" },
  sheetCancel: { alignItems: "center", paddingVertical: 12, marginTop: 2 },
  sheetCancelText: { fontSize: 15, fontWeight: "600" },
});
